import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../api/axios";

// ── Top-level page ─────────────────────────────────────────────────────────
export default function Admin() {
  const { user, loading, isQRSession } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("products");

  useEffect(() => {
    if (!loading && (!user || isQRSession)) navigate("/login");
    if (!loading && user && !user.isAdmin) navigate("/");
  }, [user, loading, isQRSession, navigate]);

  if (loading || !user?.isAdmin) return null;

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-farm-green dark:text-green-400">
        🛠️ Admin
      </h2>

      {/* Tab bar */}
      <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700
                      overflow-x-auto">
        {[
          { id: "products",  label: "📦 Products"  },
          { id: "coupons",   label: "🎟️ Coupons"   },
          { id: "orders",    label: "📋 Orders"    },
          { id: "analytics", label: "📊 Analytics" },
          { id: "credits",   label: "💰 Credits"   },
          { id: "email",     label: "✉️ Email"     },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`
              px-5 py-2.5 text-sm font-semibold border-b-2 transition-colors
              whitespace-nowrap
              ${tab === t.id
                ? "border-farm-green text-farm-green dark:border-green-400 dark:text-green-400"
                : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              }
            `}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "products"  && <ProductsTab />}
      {tab === "coupons"   && <CouponsTab />}
      {tab === "orders"    && <OrdersTab />}
      {tab === "analytics" && <AnalyticsTab />}
      {tab === "credits"   && <CreditsTab />}
      {tab === "email"     && <EmailTab />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Products tab
// ═══════════════════════════════════════════════════════════════════════════
function ProductsTab() {
  const [products, setProducts]               = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [showCreate, setShowCreate]           = useState(false);
  const [message, setMessage]                 = useState({ text: "", type: "success" });

  const loadProducts = useCallback(() => {
    setLoadingProducts(true);
    api.get("/admin/products")
      .then(({ data }) => setProducts(data))
      .catch(console.error)
      .finally(() => setLoadingProducts(false));
  }, []);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  const flash = (text, type = "success") => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: "", type: "success" }), 3000);
  };

  const toggleVariant = async (variant) => {
    try {
      await api.patch(`/admin/variants/${variant.id}`, {
        isVisible: !variant.isVisible,
      });
      loadProducts();
    } catch { flash("Update failed.", "error"); }
  };

  const updatePrice = async (variant, newPrice) => {
    const price = Number(newPrice);
    if (!price || price <= 0) {
      flash("Price must be a positive number.", "error");
      return false;
    }
    try {
      await api.patch(`/admin/variants/${variant.id}`, { price });
      flash(`Price updated to ${price} credits.`);
      loadProducts();
      return true;
    } catch {
      flash("Price update failed.", "error");
      return false;
    }
  };

  const addVariant = async (productId, label, price) => {
    try {
      await api.post(`/admin/products/${productId}/variants`, {
        label, price,
      });
      flash(`Option "${label}" added.`);
      loadProducts();
      return true;
    } catch (err) {
      flash(err.response?.data?.error || "Failed to add option.", "error");
      return false;
    }
  };

  const noVariantProducts = products.filter(
    (p) =>
      p.visibility !== "hidden" &&
      p.variants.filter((v) => v.isVisible).length === 0
  );

  return (
    <div className="space-y-5">
      {message.text && <FlashMessage message={message} />}

      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {products.length} product{products.length !== 1 ? "s" : ""}
        </p>
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="bg-farm-green text-white px-4 py-2 rounded-xl text-sm
                     font-semibold hover:bg-green-700 transition-colors"
        >
          {showCreate ? "Cancel" : "+ New Product"}
        </button>
      </div>

      {showCreate && (
        <CreateProductForm
          onCreated={() => {
            setShowCreate(false);
            loadProducts();
            flash(
              "Product created! It starts hidden — make it visible when ready."
            );
          }}
          onError={(msg) => flash(msg, "error")}
        />
      )}

      {noVariantProducts.length > 0 && (
        <div className="bg-amber-50 dark:bg-amber-900/30 border
                        border-amber-300 dark:border-amber-600 rounded-xl
                        p-4 text-sm text-amber-800 dark:text-amber-300">
          <p className="font-bold mb-1">
            ⚠️ Hidden from customers — no visible options
          </p>
          <ul className="list-disc list-inside space-y-0.5">
            {noVariantProducts.map((p) => (
              <li key={p.id} className="font-semibold">
                {p.emoji} {p.name}
              </li>
            ))}
          </ul>
        </div>
      )}

      {loadingProducts ? (
        <Spinner />
      ) : products.length === 0 && !showCreate ? (
        <div className="text-center py-12 text-gray-400 dark:text-gray-500">
          No products yet. Create your first one above.
        </div>
      ) : (
        products.map((p) => {
          const hasNoVisibleVariants =
            p.visibility !== "hidden" &&
            p.variants.filter((v) => v.isVisible).length === 0;

          return (
            <div
              key={p.id}
              className={`bg-white dark:bg-gray-800 rounded-2xl shadow-lg
                          border p-6 transition-colors
                          ${hasNoVisibleVariants
                            ? "border-amber-300 dark:border-amber-600"
                            : p.visibility === "hidden"
                            ? "border-gray-200 dark:border-gray-700 opacity-70"
                            : "border-farm-tan/30 dark:border-gray-700"
                          }`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-3xl flex-shrink-0">{p.emoji}</span>
                  <div className="min-w-0">
                    <div className="font-bold text-gray-800 dark:text-gray-100
                                    flex items-center gap-2 flex-wrap">
                      {p.name}
                      {hasNoVisibleVariants && (
                        <span className="text-xs bg-amber-100
                                         dark:bg-amber-900/50 text-amber-700
                                         dark:text-amber-400 px-2 py-0.5
                                         rounded-full font-semibold">
                          ⚠️ No options
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400
                                    mt-0.5 truncate">
                      {p.shortDescription}
                    </div>
                    <div className="text-xs text-gray-400 dark:text-gray-500
                                    font-mono mt-0.5">
                      slug: {p.slug}
                    </div>
                  </div>
                </div>

                {/* Three-way visibility selector */}
                <select
                  value={p.visibility ?? "everyone"}
                  onChange={async (e) => {
                    try {
                      await api.patch(`/admin/products/${p.id}`, {
                        visibility: e.target.value,
                      });
                      flash(
                        `"${p.name}" visibility set to ${e.target.value}.`
                      );
                      loadProducts();
                    } catch {
                      flash("Update failed.", "error");
                    }
                  }}
                  className={`flex-shrink-0 px-3 py-1.5 rounded-xl text-sm
                              font-semibold border-2 transition-colors
                              bg-white dark:bg-gray-700 focus:outline-none
                              focus:ring-2 focus:ring-farm-green
                              ${p.visibility === "everyone"
                                ? "border-green-400 text-green-700 dark:text-green-400"
                                : p.visibility === "specific"
                                ? "border-blue-400 text-blue-700 dark:text-blue-400"
                                : "border-gray-300 text-gray-500 dark:text-gray-400"
                              }`}
                >
                  <option value="everyone">✓ Everyone</option>
                  <option value="specific">🔒 Specific accounts</option>
                  <option value="hidden">Hidden</option>
                </select>
              </div>

              <div className="mt-4 space-y-2">
                {p.variants.length === 0 ? (
                  <p className="text-xs text-gray-400 dark:text-gray-500
                                italic px-1">
                    No options yet — add one below.
                  </p>
                ) : (
                  p.variants.map((v) => (
                    <VariantRow
                      key={v.id}
                      variant={v}
                      onToggle={() => toggleVariant(v)}
                      onPriceSave={(price) => updatePrice(v, price)}
                    />
                  ))
                )}
                <AddVariantForm productId={p.id} onAdd={addVariant} />
              </div>

              {p.visibility === "specific" && (
                <AccountAllowlist
                  productId={p.id}
                  onError={(msg) => flash(msg, "error")}
                  onSuccess={(msg) => flash(msg)}
                />
              )}
            </div>
          );
        })
      )}
    </div>
  );
}

// ── Create product form ────────────────────────────────────────────────────
const EMOJI_PRESETS = [
  "🥚","🍯","🍎","🫖","🥛","🧀","🎟️","🌿","🌻","🫙",
  "🥩","🥕","🌽","🫐","🍓","🍅","🌾","🐓","🐄","🛒",
];

function CreateProductForm({ onCreated, onError }) {
  const [form, setForm] = useState({
    name:             "",
    slug:             "",
    shortDescription: "",
    longDescription:  "",
    emoji:            "🛒",
    customEmoji:      false,
    visibility:       "hidden",
  });
  const [variants, setVariants]     = useState([{ label: "", price: "" }]);
  const [saving, setSaving]         = useState(false);
  const [slugManual, setSlugManual] = useState(false);

  const set = (k) => (e) => {
    const val = e.target.value;
    setForm((prev) => {
      const next = { ...prev, [k]: val };
      if (k === "name" && !slugManual) {
        next.slug = val
          .toLowerCase()
          .replace(/[^a-z0-9\s-]/g, "")
          .trim()
          .replace(/\s+/g, "-");
      }
      return next;
    });
  };

  const setSlug = (e) => {
    setSlugManual(true);
    setForm((prev) => ({
      ...prev,
      slug: e.target.value
        .toLowerCase()
        .replace(/[^a-z0-9-]/g, "")
        .replace(/\s+/g, "-"),
    }));
  };

  const addVariantRow    = () =>
    setVariants((prev) => [...prev, { label: "", price: "" }]);
  const removeVariantRow = (i) =>
    setVariants((prev) => prev.filter((_, idx) => idx !== i));
  const setVariantField  = (i, field, val) =>
    setVariants((prev) =>
      prev.map((v, idx) => (idx === i ? { ...v, [field]: val } : v))
    );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim())             return onError("Product name is required.");
    if (!form.slug.trim())             return onError("Slug is required.");
    if (!form.shortDescription.trim()) return onError("Short description is required.");

    const validVariants = variants.filter(
      (v) => v.label.trim() && Number(v.price) > 0
    );
    if (validVariants.length === 0) {
      return onError(
        "Add at least one option with a label and a price greater than 0."
      );
    }

    setSaving(true);
    try {
      await api.post("/admin/products", {
        name:             form.name.trim(),
        slug:             form.slug.trim(),
        shortDescription: form.shortDescription.trim(),
        longDescription:  form.longDescription.trim() || null,
        emoji:            form.emoji,
        visibility:       form.visibility,
        variants:         validVariants.map((v) => ({
          label: v.label.trim(),
          price: Number(v.price),
        })),
      });
      onCreated();
    } catch (err) {
      onError(err.response?.data?.error || "Failed to create product.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200
                    dark:border-green-700 rounded-2xl p-6 space-y-5">
      <h3 className="font-bold text-green-800 dark:text-green-300 text-lg">
        Create New Product
      </h3>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600
                              dark:text-gray-400 mb-1">
              Product Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text" required value={form.name}
              onChange={set("name")} placeholder="e.g. Raw Honey"
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600
                              dark:text-gray-400 mb-1">
              URL Slug <span className="text-red-500">*</span>
            </label>
            <input
              type="text" required value={form.slug}
              onChange={setSlug} placeholder="e.g. raw-honey"
              className={inputCls + " font-mono"}
            />
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
              Lowercase letters, numbers, hyphens only.
            </p>
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-gray-600
                              dark:text-gray-400 mb-1">
              Short Description <span className="text-red-500">*</span>
            </label>
            <input
              type="text" required value={form.shortDescription}
              onChange={set("shortDescription")}
              placeholder="One line shown on the products grid"
              className={inputCls}
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-gray-600
                              dark:text-gray-400 mb-1">
              Long Description{" "}
              <span className="text-gray-400 font-normal">
                (optional — shown on product page)
              </span>
            </label>
            <textarea
              rows={4} value={form.longDescription}
              onChange={set("longDescription")}
              placeholder="Detailed description shown when a customer clicks into the product…"
              className={inputCls + " resize-none"}
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-gray-600
                              dark:text-gray-400 mb-1">
              Visibility
            </label>
            <select value={form.visibility} onChange={set("visibility")}
                    className={inputCls}>
              <option value="hidden">Hidden — not visible to customers</option>
              <option value="everyone">Everyone — visible to all customers</option>
              <option value="specific">Specific accounts only</option>
            </select>
          </div>
        </div>

        {/* Emoji picker */}
        <div>
          <label className="block text-xs font-medium text-gray-600
                            dark:text-gray-400 mb-2">
            Icon / Emoji
          </label>
          <div className="flex flex-wrap gap-2 mb-2">
            {EMOJI_PRESETS.map((em) => (
              <button
                key={em} type="button"
                onClick={() =>
                  setForm((p) => ({ ...p, emoji: em, customEmoji: false }))
                }
                className={`text-xl h-10 w-10 rounded-xl border-2
                            transition-all flex items-center justify-center
                            ${form.emoji === em && !form.customEmoji
                              ? "border-farm-green bg-green-50 dark:border-green-500 dark:bg-green-900/30"
                              : "border-gray-200 dark:border-gray-600 hover:border-farm-tan bg-white dark:bg-gray-700"
                            }`}
              >
                {em}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Or type any emoji:
            </span>
            <input
              type="text"
              value={form.customEmoji ? form.emoji : ""}
              onChange={(e) => {
                const val = e.target.value.trim();
                if (val) {
                  setForm((p) => ({ ...p, emoji: val, customEmoji: true }));
                } else {
                  setForm((p) => ({ ...p, customEmoji: false, emoji: "🛒" }));
                }
              }}
              placeholder="✨" maxLength={4}
              className="w-16 border border-gray-300 dark:border-gray-600
                         rounded-lg px-2 py-1 text-center text-xl
                         bg-white dark:bg-gray-700 text-gray-800
                         dark:text-gray-100 focus:outline-none
                         focus:ring-2 focus:ring-farm-green"
            />
            <div className="text-2xl w-10 text-center">{form.emoji}</div>
          </div>
        </div>

        {/* Variants */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-medium text-gray-600
                              dark:text-gray-400">
              Options / Variants <span className="text-red-500">*</span>
            </label>
            <button
              type="button" onClick={addVariantRow}
              className="text-xs text-farm-green dark:text-green-400
                         hover:underline font-semibold"
            >
              + Add another option
            </button>
          </div>
          <div className="space-y-2">
            {variants.map((v, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  type="text" value={v.label}
                  onChange={(e) => setVariantField(i, "label", e.target.value)}
                  placeholder={`Option label, e.g. "8 oz jar"`}
                  className={inputCls + " flex-1"}
                />
                <div className="relative flex-shrink-0 w-32">
                  <input
                    type="number" min={0.5} step={0.5} value={v.price}
                    onChange={(e) =>
                      setVariantField(i, "price", e.target.value)
                    }
                    placeholder="Price"
                    className={inputCls + " pr-14"}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2
                                   text-xs text-gray-400 dark:text-gray-500
                                   pointer-events-none">
                    credits
                  </span>
                </div>
                {variants.length > 1 && (
                  <button
                    type="button" onClick={() => removeVariantRow(i)}
                    className="flex-shrink-0 text-gray-400 hover:text-red-500
                               dark:hover:text-red-400 transition-colors
                               text-lg leading-none"
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1.5">
            Only rows with both a label and a price greater than 0 will
            be saved.
          </p>
        </div>

        {/* Preview */}
        {form.name && (
          <div className="border-2 border-dashed border-green-300
                          dark:border-green-700 rounded-xl p-4">
            <p className="text-xs font-medium text-green-700
                          dark:text-green-400 mb-2">
              Preview (how it will appear on the Products page)
            </p>
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4
                            border border-gray-100 dark:border-gray-700">
              <div className="text-4xl mb-2">{form.emoji}</div>
              <div className="font-bold text-gray-800 dark:text-gray-100">
                {form.name}
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400
                            mt-1 mb-2">
                {form.shortDescription || "Short description will appear here."}
              </p>
              <div className="text-sm font-bold text-farm-green
                              dark:text-green-400">
                {variants.filter((v) => Number(v.price) > 0).length > 0
                  ? `From ${Math.min(
                      ...variants
                        .filter((v) => Number(v.price) > 0)
                        .map((v) => Number(v.price))
                    )} credits`
                  : "— credits"}
              </div>
            </div>
          </div>
        )}

        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200
                        dark:border-blue-700 rounded-xl p-3 text-xs
                        text-blue-700 dark:text-blue-300">
          💡 New products are created as <strong>hidden</strong> by default.
          Toggle visibility from the product list when ready.
        </div>

        <button
          type="submit" disabled={saving}
          className="w-full bg-farm-green text-white py-3 rounded-xl
                     font-semibold hover:bg-green-700 transition-colors
                     disabled:opacity-60"
        >
          {saving ? "Creating…" : "Create Product"}
        </button>
      </form>
    </div>
  );
}

// ── Account allowlist ──────────────────────────────────────────────────────
function AccountAllowlist({ productId, onError, onSuccess }) {
  const [accounts, setAccounts] = useState([]);
  const [input, setInput]       = useState("");
  const [adding, setAdding]     = useState(false);

  const load = useCallback(() => {
    api.get(`/admin/products/${productId}/accounts`)
      .then(({ data }) => setAccounts(data))
      .catch(console.error);
  }, [productId]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!input.trim()) return;
    setAdding(true);
    try {
      const { data } = await api.post(
        `/admin/products/${productId}/accounts`,
        { accountNumber: input.trim().toUpperCase() }
      );
      onSuccess(`Added ${data.customerName ?? data.accountNumber}`);
      setInput("");
      load();
    } catch (err) {
      onError(err.response?.data?.error || "Failed to add account.");
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (accountNumber) => {
    try {
      await api.delete(
        `/admin/products/${productId}/accounts/${accountNumber}`
      );
      load();
    } catch {
      onError("Failed to remove account.");
    }
  };

  return (
    <div className="mt-3 bg-blue-50 dark:bg-blue-900/20 border
                    border-blue-200 dark:border-blue-700 rounded-xl p-4">
      <p className="text-xs font-semibold text-blue-800 dark:text-blue-300
                    mb-3">
        🔒 Allowed Accounts — only these customers can see this product
      </p>
      {accounts.length > 0 ? (
        <div className="space-y-1.5 mb-3">
          {accounts.map((a) => (
            <div
              key={a.id}
              className="flex items-center justify-between bg-white
                         dark:bg-gray-700 rounded-lg px-3 py-2 border
                         border-blue-100 dark:border-gray-600"
            >
              <span className="font-mono text-xs text-gray-700
                               dark:text-gray-300">
                {a.accountNumber}
              </span>
              <button
                type="button"
                onClick={() => handleRemove(a.accountNumber)}
                className="text-xs text-red-500 dark:text-red-400
                           hover:underline ml-3"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-blue-600 dark:text-blue-400 mb-3 italic">
          No accounts added yet — product is effectively hidden until you
          add at least one account.
        </p>
      )}
      <div className="flex gap-2">
        <input
          type="text" value={input}
          onChange={(e) => setInput(e.target.value.toUpperCase())}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); handleAdd(); }
          }}
          placeholder="CFF-123456" maxLength={20}
          className="flex-1 border border-blue-300 dark:border-blue-600
                     rounded-lg px-3 py-1.5 text-sm font-mono
                     bg-white dark:bg-gray-700 text-gray-800
                     dark:text-gray-100 focus:outline-none
                     focus:ring-2 focus:ring-farm-green"
        />
        <button
          type="button" disabled={adding || !input.trim()}
          onClick={handleAdd}
          className="px-3 py-1.5 bg-farm-green text-white text-xs
                     font-semibold rounded-lg hover:bg-green-700
                     transition-colors disabled:opacity-50"
        >
          {adding ? "…" : "Add"}
        </button>
      </div>
      <p className="text-xs text-blue-600 dark:text-blue-400 mt-1.5">
        Enter the customer's account number (e.g. CFF-123456).
      </p>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Coupons tab
// ═══════════════════════════════════════════════════════════════════════════
function CouponsTab() {
  const [coupons, setCoupons]               = useState([]);
  const [customers, setCustomers]           = useState([]);
  const [products, setProducts]             = useState([]);
  const [loadingCoupons, setLoadingCoupons] = useState(true);
  const [showCreate, setShowCreate]         = useState(false);
  const [sendModal, setSendModal]           = useState(null);
  const [message, setMessage]               = useState({ text: "", type: "success" });

  const flash = (text, type = "success") => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: "", type: "success" }), 4000);
  };

  const loadCoupons = useCallback(() => {
    setLoadingCoupons(true);
    api.get("/admin/coupons")
      .then(({ data }) => setCoupons(data))
      .catch(console.error)
      .finally(() => setLoadingCoupons(false));
  }, []);

  const loadCustomers = useCallback(() => {
    api.get("/admin/customers")
      .then(({ data }) => setCustomers(data))
      .catch(console.error);
  }, []);

  const loadProducts = useCallback(() => {
    api.get("/admin/products")
      .then(({ data }) => setProducts(data))
      .catch(console.error);
  }, []);

  useEffect(() => {
    loadCoupons();
    loadCustomers();
    loadProducts();
  }, [loadCoupons, loadCustomers, loadProducts]);

  const handleToggleActive = async (coupon) => {
    try {
      await api.patch(`/admin/coupons/${coupon.id}`, {
        isActive: !coupon.isActive,
      });
      flash(
        `"${coupon.code}" is now ${coupon.isActive ? "deactivated" : "active"}.`
      );
      loadCoupons();
    } catch (err) {
      flash(err.response?.data?.error || "Update failed.", "error");
    }
  };

  const handleDelete = async (coupon) => {
    if (
      !window.confirm(
        `Delete coupon "${coupon.code}"? This cannot be undone.`
      )
    ) return;
    try {
      await api.delete(`/admin/coupons/${coupon.id}`);
      flash(`"${coupon.code}" deleted.`);
      loadCoupons();
    } catch (err) {
      flash(err.response?.data?.error || "Delete failed.", "error");
    }
  };

  return (
    <div className="space-y-5">
      {message.text && <FlashMessage message={message} />}

      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {coupons.length} coupon{coupons.length !== 1 ? "s" : ""}
        </p>
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="bg-farm-green text-white px-4 py-2 rounded-xl text-sm
                     font-semibold hover:bg-green-700 transition-colors"
        >
          {showCreate ? "Cancel" : "+ New Coupon"}
        </button>
      </div>

      {showCreate && (
        <CreateCouponForm
          products={products}
          onCreated={() => {
            loadCoupons();
            setShowCreate(false);
            flash("Coupon created successfully!");
          }}
          onError={(msg) => flash(msg, "error")}
        />
      )}

      {loadingCoupons ? (
        <Spinner />
      ) : coupons.length === 0 ? (
        <div className="text-center py-12 text-gray-400 dark:text-gray-500">
          No coupons yet. Create your first one above.
        </div>
      ) : (
        <div className="space-y-4">
          {coupons.map((c) => (
            <CouponCard
              key={c.id}
              coupon={c}
              onToggleActive={() => handleToggleActive(c)}
              onDelete={() => handleDelete(c)}
              onSend={() => setSendModal(c)}
            />
          ))}
        </div>
      )}

      {sendModal && (
        <SendCouponModal
          coupon={sendModal}
          customers={customers}
          onClose={() => setSendModal(null)}
          onSent={(msg) => { setSendModal(null); flash(msg); }}
          onError={(msg) => flash(msg, "error")}
        />
      )}
    </div>
  );
}

// ── Create coupon form ─────────────────────────────────────────────────────
function CreateCouponForm({ products = [], onCreated, onError }) {
  const [form, setForm] = useState({
    code:       "",
    type:       "fixed",
    value:      "",
    usageLimit: "",
    expiresAt:  "",
    productId:  "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/coupons", {
        code:       form.code.trim().toUpperCase(),
        type:       form.type,
        value:      Number(form.value),
        usageLimit: form.usageLimit ? Number(form.usageLimit) : null,
        expiresAt:  form.expiresAt || null,
        productId:  form.productId || null,
      });
      setForm({
        code: "", type: "fixed", value: "",
        usageLimit: "", expiresAt: "", productId: "",
      });
      onCreated();
    } catch (err) {
      onError(err.response?.data?.error || "Failed to create coupon.");
    } finally {
      setSaving(false);
    }
  };

  const selectedProduct = products.find((p) => p.id === form.productId);

  return (
    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200
                    dark:border-blue-700 rounded-2xl p-6">
      <h3 className="font-bold text-blue-800 dark:text-blue-300 mb-4">
        Create New Coupon
      </h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2 sm:col-span-1">
            <label className="block text-xs font-medium text-gray-600
                              dark:text-gray-400 mb-1">
              Coupon Code <span className="text-red-500">*</span>
            </label>
            <input
              type="text" required value={form.code}
              onChange={(e) =>
                setForm({ ...form, code: e.target.value.toUpperCase() })
              }
              placeholder="e.g. SUMMER20" maxLength={32}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600
                              dark:text-gray-400 mb-1">
              Discount Type <span className="text-red-500">*</span>
            </label>
            <select value={form.type} onChange={set("type")} className={inputCls}>
              <option value="fixed">Fixed credits off</option>
              <option value="percentage">Percentage off</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600
                              dark:text-gray-400 mb-1">
              {form.type === "fixed" ? "Credits Off *" : "Percentage (%) *"}
            </label>
            <input
              type="number" required min={0.5}
              max={form.type === "percentage" ? 100 : undefined}
              step={form.type === "percentage" ? 1 : 0.5}
              value={form.value} onChange={set("value")}
              placeholder={form.type === "fixed" ? "e.g. 2" : "e.g. 20"}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600
                              dark:text-gray-400 mb-1">
              Usage Limit
            </label>
            <input
              type="number" min={1} value={form.usageLimit}
              onChange={set("usageLimit")} placeholder="Blank = unlimited"
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600
                              dark:text-gray-400 mb-1">
              Expiry Date
            </label>
            <input
              type="date" value={form.expiresAt} onChange={set("expiresAt")}
              min={new Date().toISOString().split("T")[0]}
              className={inputCls}
            />
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-600
                              dark:text-gray-400 mb-1">
              Valid For
            </label>
            <select
              value={form.productId} onChange={set("productId")}
              className={inputCls}
            >
              <option value="">All products (no restriction)</option>
              {products
                .filter((p) => p.visibility !== "hidden")
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.emoji} {p.name}
                  </option>
                ))}
            </select>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
              {form.productId
                ? "Customers will see a clear error message telling them " +
                  "which product this coupon is valid for if they try to " +
                  "use it on another product."
                : "This coupon will be accepted on any product."}
            </p>
          </div>
        </div>

        {form.code && form.value && (
          <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border
                          border-blue-200 dark:border-blue-700 text-sm
                          text-gray-700 dark:text-gray-300">
            <span className="font-mono font-bold text-farm-green
                             dark:text-green-400">
              {form.code}
            </span>{" "}
            gives{" "}
            <strong>
              {form.type === "fixed"
                ? `${form.value} credit${Number(form.value) !== 1 ? "s" : ""} off`
                : `${form.value}% off`}
            </strong>
            {selectedProduct
              ? <> — only valid on <strong>{selectedProduct.emoji} {selectedProduct.name}</strong></>
              : " — valid on all products"}
            {form.usageLimit ? `, ${form.usageLimit}-use limit` : ""}
            {form.expiresAt
              ? `, expires ${new Date(form.expiresAt).toLocaleDateString()}`
              : ""}
          </div>
        )}

        <button
          type="submit" disabled={saving}
          className="w-full bg-farm-green text-white py-2.5 rounded-xl
                     font-semibold hover:bg-green-700 transition-colors
                     disabled:opacity-60"
        >
          {saving ? "Creating…" : "Create Coupon"}
        </button>
      </form>
    </div>
  );
}

// ── Coupon card ────────────────────────────────────────────────────────────
function CouponCard({ coupon, onToggleActive, onDelete, onSend }) {
  const isExpired =
    coupon.expiresAt && new Date(coupon.expiresAt) < new Date();
  const usageDisplay =
    coupon.usageLimit !== null
      ? `${coupon.usageCount} / ${coupon.usageLimit} uses`
      : `${coupon.usageCount} uses`;

  return (
    <div
      className={`bg-white dark:bg-gray-800 rounded-2xl border p-5
                  transition-colors
                  ${!coupon.isActive || isExpired
                    ? "border-gray-200 dark:border-gray-700 opacity-70"
                    : "border-farm-tan/30 dark:border-gray-700"
                  }`}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono font-bold text-xl text-farm-green
                             dark:text-green-400 tracking-wider">
              {coupon.code}
            </span>
            {!coupon.isActive && (
              <span className="text-xs bg-gray-100 dark:bg-gray-700
                               text-gray-500 dark:text-gray-400 px-2 py-0.5
                               rounded-full">
                Deactivated
              </span>
            )}
            {isExpired && (
              <span className="text-xs bg-red-100 dark:bg-red-900/40
                               text-red-600 dark:text-red-400 px-2 py-0.5
                               rounded-full">
                Expired
              </span>
            )}
          </div>
          <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-sm
                          text-gray-600 dark:text-gray-400">
            <span>
              {coupon.type === "fixed"
                ? `${coupon.value} credit${coupon.value !== 1 ? "s" : ""} off`
                : `${coupon.value}% off`}
            </span>
            <span>{usageDisplay}</span>
            {coupon.expiresAt && (
              <span className={isExpired ? "text-red-500 dark:text-red-400" : ""}>
                Expires{" "}
                {new Date(coupon.expiresAt).toLocaleDateString("en-US", {
                  month: "short", day: "numeric", year: "numeric",
                })}
              </span>
            )}
          </div>
          {coupon.productName ? (
            <div className="mt-2 inline-flex items-center gap-1.5 bg-blue-50
                            dark:bg-blue-900/30 border border-blue-200
                            dark:border-blue-700 text-blue-700
                            dark:text-blue-300 text-xs font-semibold
                            px-2.5 py-1 rounded-full">
              <span>🎯</span>
              <span>Only valid for: {coupon.productName}</span>
            </div>
          ) : (
            <div className="mt-2 inline-flex items-center gap-1.5 bg-gray-50
                            dark:bg-gray-700 border border-gray-200
                            dark:border-gray-600 text-gray-500
                            dark:text-gray-400 text-xs px-2.5 py-1
                            rounded-full">
              <span>🛒</span>
              <span>Valid on all products</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
          <button
            onClick={onSend}
            disabled={!coupon.isActive || isExpired}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl
                       text-sm font-semibold border-2 border-farm-green
                       text-farm-green dark:border-green-400
                       dark:text-green-400 hover:bg-farm-green
                       hover:text-white dark:hover:bg-green-700
                       transition-colors disabled:opacity-40
                       disabled:cursor-not-allowed"
          >
            📧 Send
          </button>
          <button
            onClick={onToggleActive}
            className={`px-3 py-1.5 rounded-xl text-sm font-semibold
                        transition-colors
                        ${coupon.isActive
                          ? "bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 hover:bg-red-100 dark:hover:bg-red-900/30 hover:text-red-700 dark:hover:text-red-400"
                          : "bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-green-100 dark:hover:bg-green-900/40 hover:text-green-700 dark:hover:text-green-400"
                        }`}
          >
            {coupon.isActive ? "Active" : "Inactive"}
          </button>
          <button
            onClick={onDelete}
            className="p-1.5 rounded-xl text-gray-400 dark:text-gray-500
                       hover:text-red-500 dark:hover:text-red-400
                       hover:bg-red-50 dark:hover:bg-red-900/20
                       transition-colors"
          >
            🗑️
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Send coupon modal ──────────────────────────────────────────────────────
function SendCouponModal({ coupon, customers, onClose, onSent, onError }) {
  const [mode, setMode]                     = useState("select");
  const [search, setSearch]                 = useState("");
  const [selected, setSelected]             = useState([]);
  const [personalMessage, setPersonalMessage] = useState("");
  const [sending, setSending]               = useState(false);

  const filtered = customers.filter((c) => {
    const q = search.toLowerCase();
    return (
      c.firstName.toLowerCase().includes(q) ||
      c.lastName.toLowerCase().includes(q) ||
      c.email.toLowerCase().includes(q) ||
      c.accountNumber.toLowerCase().includes(q)
    );
  });

  const toggleCustomer = (id) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const handleSend = async () => {
    if (mode === "select" && selected.length === 0) {
      onError("Select at least one customer.");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post(`/admin/coupons/${coupon.id}/send`, {
        customerIds: mode === "select" ? selected : [],
        sendToAll:   mode === "all",
        message:     personalMessage,
      });
      onSent(data.message);
    } catch (err) {
      onError(err.response?.data?.error || "Failed to send.");
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center
                 bg-black/50 px-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl
                      w-full max-w-lg max-h-[90vh] flex flex-col
                      border border-farm-tan/30 dark:border-gray-700">
        <div className="flex items-center justify-between p-6 border-b
                        border-gray-100 dark:border-gray-700">
          <div>
            <h3 className="font-bold text-farm-green dark:text-green-400 text-lg">
              Send Coupon
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              <span className="font-mono font-bold">{coupon.code}</span>{" "}
              &mdash;{" "}
              {coupon.type === "fixed"
                ? `${coupon.value} credits off`
                : `${coupon.value}% off`}
              {coupon.productName && (
                <span className="ml-1 text-blue-600 dark:text-blue-400">
                  (only for {coupon.productName})
                </span>
              )}
            </p>
          </div>
          <button onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
            ×
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700
                              dark:text-gray-300 mb-2">
              Send to
            </label>
            <div className="grid grid-cols-2 gap-3">
              {[
                { id: "select", label: "🎯 Specific customers" },
                { id: "all",    label: "📣 All customers"      },
              ].map((m) => (
                <button
                  key={m.id} type="button" onClick={() => setMode(m.id)}
                  className={`p-3 rounded-xl border-2 text-sm font-medium
                              transition-all
                              ${mode === m.id
                                ? "border-farm-green bg-green-50 text-farm-green dark:border-green-500 dark:bg-green-900/30 dark:text-green-400"
                                : "border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-farm-tan"
                              }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          {mode === "select" && (
            <div>
              <label className="block text-sm font-medium text-gray-700
                                dark:text-gray-300 mb-2">
                Select customers{" "}
                {selected.length > 0 && (
                  <span className="text-farm-green dark:text-green-400">
                    ({selected.length} selected)
                  </span>
                )}
              </label>
              <input
                type="text" value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search…" className={inputCls + " mb-2"}
              />
              <div className="flex gap-3 mb-2">
                <button type="button"
                        onClick={() => setSelected(filtered.map((c) => c.id))}
                        className="text-xs text-farm-green dark:text-green-400 hover:underline">
                  Select all shown
                </button>
                {selected.length > 0 && (
                  <button type="button" onClick={() => setSelected([])}
                          className="text-xs text-gray-400 hover:underline">
                    Clear
                  </button>
                )}
              </div>
              <div className="border border-gray-200 dark:border-gray-600
                              rounded-xl overflow-hidden max-h-52 overflow-y-auto">
                {filtered.map((c) => {
                  const isSel = selected.includes(c.id);
                  return (
                    <button
                      key={c.id} type="button"
                      onClick={() => toggleCustomer(c.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3
                                  text-left border-b last:border-b-0
                                  border-gray-100 dark:border-gray-700
                                  transition-colors
                                  ${isSel
                                    ? "bg-green-50 dark:bg-green-900/20"
                                    : "hover:bg-gray-50 dark:hover:bg-gray-700/50"
                                  }`}
                    >
                      <div className={`h-5 w-5 rounded border-2 flex-shrink-0
                                       flex items-center justify-center
                                       ${isSel
                                         ? "bg-farm-green border-farm-green dark:bg-green-600 dark:border-green-600"
                                         : "border-gray-300 dark:border-gray-600"
                                       }`}>
                        {isSel && (
                          <svg className="h-3 w-3 text-white"
                               viewBox="0 0 12 12" fill="currentColor">
                            <path d="M10 3L5 8.5 2 5.5" stroke="white"
                                  strokeWidth="2" fill="none"
                                  strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold text-sm text-gray-800
                                        dark:text-gray-100 truncate">
                          {c.firstName} {c.lastName}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {c.email}
                        </div>
                      </div>
                      <div className="ml-auto font-mono text-xs text-gray-400
                                      dark:text-gray-500 flex-shrink-0">
                        {c.accountNumber}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {mode === "all" && (
            <div className="bg-amber-50 dark:bg-amber-900/30 border
                            border-amber-300 dark:border-amber-600 rounded-xl
                            p-4 text-sm text-amber-800 dark:text-amber-300">
              📣 This sends to all <strong>{customers.length}</strong> customers.
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700
                              dark:text-gray-300 mb-1">
              Personal message{" "}
              <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea rows={3} value={personalMessage}
                      onChange={(e) => setPersonalMessage(e.target.value)}
                      placeholder="Add a personal note…"
                      className={inputCls + " resize-none"} />
          </div>
        </div>

        <div className="p-6 border-t border-gray-100 dark:border-gray-700 flex gap-3">
          <button onClick={onClose}
                  className="flex-1 border-2 border-gray-300 dark:border-gray-600
                             text-gray-600 dark:text-gray-400 py-2.5 rounded-xl
                             font-semibold hover:bg-gray-50 dark:hover:bg-gray-700
                             transition-colors">
            Cancel
          </button>
          <button onClick={handleSend}
                  disabled={sending || (mode === "select" && selected.length === 0)}
                  className="flex-1 bg-farm-green text-white py-2.5 rounded-xl
                             font-semibold hover:bg-green-700 transition-colors
                             disabled:opacity-60">
            {sending
              ? "Sending…"
              : mode === "all"
              ? `Send to All ${customers.length}`
              : `Send to ${selected.length}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Orders tab
// ═══════════════════════════════════════════════════════════════════════════
const STATUS_CONFIG = {
  "ordered": {
    label:     "Ordered",
    emoji:     "📥",
    color:     "bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-600",
    dot:       "bg-blue-500",
    next:      "in-progress",
    nextLabel: "Start →",
  },
  "in-progress": {
    label:     "In Progress",
    emoji:     "⚙️",
    color:     "bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-600",
    dot:       "bg-amber-500",
    next:      "ready",
    nextLabel: "Mark Ready →",
  },
  "ready": {
    label:     "Ready for Pickup",
    emoji:     "✅",
    color:     "bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 border-green-300 dark:border-green-600",
    dot:       "bg-green-500",
    next:      "completed",
    nextLabel: "Mark Complete →",
  },
  "completed": {
    label:     "Completed",
    emoji:     "🎉",
    color:     "bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-600",
    dot:       "bg-gray-400",
    next:      null,
    nextLabel: null,
  },
  "cancelled": {
    label:     "Cancelled",
    emoji:     "🚫",
    color:     "bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400 border-red-300 dark:border-red-600",
    dot:       "bg-red-500",
    next:      null,
    nextLabel: null,
  },
};

function OrdersTab() {
  const [orders, setOrders]             = useState([]);
  const [loading, setLoading]           = useState(true);
  const [message, setMessage]           = useState({ text: "", type: "success" });
  const [filter, setFilter]             = useState("active");
  const [search, setSearch]             = useState("");
  const [expandedId, setExpandedId]     = useState(null);
  const [lockBoxModal, setLockBoxModal] = useState(null);

  const flash = (text, type = "success") => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: "", type: "success" }), 3500);
  };

  const loadOrders = useCallback(() => {
    setLoading(true);
    api.get("/admin/orders")
      .then(({ data }) => setOrders(data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  const updateStatus = async (orderId, newStatus, extras = {}) => {
    try {
      const { data: updated } = await api.patch(
        `/admin/orders/${orderId}/status`,
        { status: newStatus, ...extras }
      );
      setOrders((prev) =>
        prev.map((o) => (o.id === orderId ? updated : o))
      );
      flash(
        `Order marked as "${STATUS_CONFIG[newStatus]?.label ?? newStatus}".`
      );
    } catch (err) {
      flash(err.response?.data?.error || "Status update failed.", "error");
    }
  };

  const handleMarkReady = (order) => {
    setLockBoxModal(order);
  };

  const handleLockBoxConfirm = async ({ lockBox, lockBoxPin }) => {
    if (!lockBoxModal) return;
    await updateStatus(lockBoxModal.id, "ready", { lockBox, lockBoxPin });
    setLockBoxModal(null);
  };

  const activeStatuses = ["ordered", "in-progress", "ready"];

  const filteredOrders = orders.filter((o) => {
    if (search.trim()) {
      const q = search.toLowerCase();
      const matchesText =
        o.user.firstName.toLowerCase().includes(q) ||
        o.user.lastName.toLowerCase().includes(q) ||
        o.user.email.toLowerCase().includes(q) ||
        o.user.accountNumber.toLowerCase().includes(q) ||
        o.productName.toLowerCase().includes(q) ||
        o.variantLabel.toLowerCase().includes(q) ||
        (o.notes ?? "").toLowerCase().includes(q);
      if (!matchesText) return false;
    }
    if (filter === "active") return activeStatuses.includes(o.status);
    if (filter === "all")    return true;
    return o.status === filter;
  });

  const counts = orders.reduce((acc, o) => {
    acc[o.status] = (acc[o.status] ?? 0) + 1;
    return acc;
  }, {});
  const activeCount =
    (counts["ordered"] ?? 0) +
    (counts["in-progress"] ?? 0) +
    (counts["ready"] ?? 0);

  const FILTERS = [
    { id: "active",      label: "Active",          count: activeCount },
    { id: "ordered",     label: "📥 Ordered",      count: counts["ordered"]      ?? 0 },
    { id: "in-progress", label: "⚙️ In Progress",  count: counts["in-progress"] ?? 0 },
    { id: "ready",       label: "✅ Ready",         count: counts["ready"]        ?? 0 },
    { id: "completed",   label: "🎉 Completed",     count: counts["completed"]    ?? 0 },
    { id: "cancelled",   label: "🚫 Cancelled",     count: counts["cancelled"]    ?? 0 },
    { id: "all",         label: "All",              count: orders.length },
  ];

  return (
    <div className="space-y-5">
      {message.text && <FlashMessage message={message} />}

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-bold text-farm-green dark:text-green-400">
            Order Fulfillment
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Completed and cancelled orders disappear 14 days after being
            marked. Sorted by pickup date.
          </p>
        </div>
        <button
          onClick={loadOrders}
          className="text-xs text-farm-green dark:text-green-400
                     hover:underline font-semibold"
        >
          ↺ Refresh
        </button>
      </div>

      {/* Filter bar */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`
              flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs
              font-semibold whitespace-nowrap transition-colors flex-shrink-0
              ${filter === f.id
                ? "bg-farm-green text-white dark:bg-green-700"
                : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600"
              }
            `}
          >
            {f.label}
            {f.count > 0 && (
              <span className={`
                px-1.5 py-0.5 rounded-full text-xs font-bold leading-none
                ${filter === f.id
                  ? "bg-white/20 text-white"
                  : "bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300"
                }
              `}>
                {f.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Search */}
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search by customer name, email, product, or notes…"
        className={inputCls}
      />

      {/* Orders list */}
      {loading ? (
        <Spinner />
      ) : filteredOrders.length === 0 ? (
        <div className="text-center py-12 text-gray-400 dark:text-gray-500">
          {search.trim()
            ? "No orders match your search."
            : filter === "active"
            ? "🎉 No active orders right now."
            : `No ${filter} orders.`}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredOrders.map((order) => (
            <OrderCard
              key={order.id}
              order={order}
              expanded={expandedId === order.id}
              onToggleExpand={() =>
                setExpandedId(expandedId === order.id ? null : order.id)
              }
              onStatusChange={updateStatus}
              onMarkReady={handleMarkReady}
            />
          ))}
        </div>
      )}

      {lockBoxModal && (
        <LockBoxModal
          order={lockBoxModal}
          onConfirm={handleLockBoxConfirm}
          onCancel={() => setLockBoxModal(null)}
        />
      )}
    </div>
  );
}

// ── Lock box modal ─────────────────────────────────────────────────────────
function LockBoxModal({ order, onConfirm, onCancel }) {
  const [lockBox, setLockBox] = useState("");
  const [pin, setPin]         = useState("");
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState("");

  const LOCK_BOXES = [
    { id: "lockbox-1", label: "Lock Box 1" },
    { id: "lockbox-2", label: "Lock Box 2" },
    { id: "lockbox-3", label: "Lock Box 3" },
    { id: "lockbox-4", label: "Lock Box 4" },
    { id: "lockbox-5", label: "Lock Box 5" },
    { id: "manual",    label: "Manually Delivered" },
  ];

  const isManual = lockBox === "manual";

  const handleConfirm = async () => {
    setError("");
    if (!lockBox) return setError("Please select a delivery method.");
    if (!isManual && !/^\d{4,8}$/.test(pin)) {
      return setError("PIN must be 4–8 digits.");
    }
    setSaving(true);
    await onConfirm({ lockBox, lockBoxPin: isManual ? null : pin });
    setSaving(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center
                 bg-black/60 px-4"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl
                      w-full max-w-sm border border-farm-tan/30
                      dark:border-gray-700">
        <div className="flex items-center justify-between p-5 border-b
                        border-gray-100 dark:border-gray-700">
          <div>
            <h3 className="font-bold text-farm-green dark:text-green-400 text-lg">
              Mark Ready for Pickup
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {order.user.firstName} {order.user.lastName} —{" "}
              {order.productName}
            </p>
          </div>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        <div className="p-5 space-y-4">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/30 border
                            border-red-300 dark:border-red-600 text-red-700
                            dark:text-red-300 rounded-lg p-3 text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700
                              dark:text-gray-300 mb-2">
              Delivery Method <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              {LOCK_BOXES.map((lb) => (
                <button
                  key={lb.id}
                  type="button"
                  onClick={() => {
                    setLockBox(lb.id);
                    setPin("");
                    setError("");
                  }}
                  className={`
                    py-2.5 px-3 rounded-xl border-2 text-sm font-medium
                    transition-all text-left
                    ${lockBox === lb.id
                      ? lb.id === "manual"
                        ? "border-gray-400 bg-gray-50 text-gray-700 dark:border-gray-500 dark:bg-gray-700 dark:text-gray-200"
                        : "border-farm-green bg-green-50 text-farm-green dark:border-green-500 dark:bg-green-900/30 dark:text-green-400"
                      : "border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-700 hover:border-farm-tan"
                    }
                  `}
                >
                  {lb.id === "manual" ? "🤝 " : "📦 "}
                  {lb.label}
                </button>
              ))}
            </div>
          </div>

          {lockBox && !isManual && (
            <div>
              <label className="block text-sm font-medium text-gray-700
                                dark:text-gray-300 mb-1">
                Lock Box PIN{" "}
                <span className="text-gray-400 font-normal">(4–8 digits)</span>
                <span className="text-red-500"> *</span>
              </label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={8}
                value={pin}
                onChange={(e) =>
                  setPin(e.target.value.replace(/\D/g, "").slice(0, 8))
                }
                placeholder="e.g. 1234"
                autoFocus
                className="w-full border border-gray-300 dark:border-gray-600
                           rounded-lg px-3 py-3 text-center text-2xl
                           tracking-[0.4em] font-mono bg-white dark:bg-gray-700
                           text-gray-800 dark:text-gray-100
                           focus:outline-none focus:ring-2 focus:ring-farm-green"
              />
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                This PIN will be sent to{" "}
                <strong>{order.user.firstName}</strong> in a pickup
                notification email.
              </p>
            </div>
          )}

          {isManual && (
            <div className="bg-gray-50 dark:bg-gray-700/50 border
                            border-gray-200 dark:border-gray-600 rounded-xl
                            p-3 text-sm text-gray-600 dark:text-gray-400">
              🤝 No email will be sent — you will deliver this order
              directly to the customer.
            </div>
          )}

          {lockBox && !isManual && pin.length >= 4 && (
            <div className="bg-green-50 dark:bg-green-900/20 border
                            border-green-200 dark:border-green-700 rounded-xl
                            p-3 text-sm text-green-700 dark:text-green-300">
              📧 An email will be sent to{" "}
              <strong>{order.user.firstName}</strong> telling them their
              order is in{" "}
              <strong>
                {LOCK_BOXES.find((lb) => lb.id === lockBox)?.label}
              </strong>{" "}
              with PIN{" "}
              <strong className="font-mono tracking-wider">{pin}</strong>.
            </div>
          )}
        </div>

        <div className="p-5 border-t border-gray-100 dark:border-gray-700
                        flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 border-2 border-gray-300 dark:border-gray-600
                       text-gray-600 dark:text-gray-400 py-2.5 rounded-xl
                       font-semibold hover:bg-gray-50 dark:hover:bg-gray-700
                       transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={saving || !lockBox || (!isManual && pin.length < 4)}
            className="flex-1 bg-farm-green text-white py-2.5 rounded-xl
                       font-semibold hover:bg-green-700 transition-colors
                       disabled:opacity-60"
          >
            {saving ? "Saving…" : "Mark Ready ✅"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Order card ─────────────────────────────────────────────────────────────
function OrderCard({
  order, expanded, onToggleExpand, onStatusChange, onMarkReady,
}) {
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const cfg = STATUS_CONFIG[order.status] ?? STATUS_CONFIG["ordered"];

  const handleStatusChange = async (newStatus) => {
    if (newStatus === "ready") {
      onMarkReady(order);
      return;
    }
    setUpdatingStatus(true);
    await onStatusChange(order.id, newStatus);
    setUpdatingStatus(false);
  };

  const pickupDate = new Date(order.desiredDate);
  const today      = new Date();
  today.setHours(0, 0, 0, 0);
  const pickupDay  = new Date(pickupDate);
  pickupDay.setHours(0, 0, 0, 0);
  const daysUntilPickup = Math.round(
    (pickupDay - today) / (1000 * 60 * 60 * 24)
  );

  const pickupLabel =
    daysUntilPickup === 0  ? "Today"
    : daysUntilPickup === 1 ? "Tomorrow"
    : daysUntilPickup < 0  ? `${Math.abs(daysUntilPickup)}d overdue`
    :                         `In ${daysUntilPickup}d`;

  const pickupUrgency =
    daysUntilPickup < 0   ? "text-red-500 dark:text-red-400"
    : daysUntilPickup === 0 ? "text-amber-600 dark:text-amber-400 font-bold"
    : daysUntilPickup <= 2  ? "text-amber-500 dark:text-amber-400"
    :                         "text-gray-500 dark:text-gray-400";

  const isFinished = order.status === "completed" || order.status === "cancelled";

  const lockBoxLabel =
    order.lockBox === "manual"
      ? "Manually Delivered"
      : order.lockBox
      ? `Lock Box ${order.lockBox.replace("lockbox-", "")}`
      : null;

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-2xl border
                     transition-all
                     ${isFinished
                       ? "border-gray-200 dark:border-gray-700 opacity-80"
                       : "border-farm-tan/40 dark:border-gray-700 shadow-sm"
                     }`}>
      <div className="flex items-start gap-3 p-4">
        <div className="flex-shrink-0 mt-1">
          <div className={`h-3 w-3 rounded-full ${cfg.dot}`} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div className="min-w-0">
              <div className="font-bold text-gray-800 dark:text-gray-100">
                {order.user.firstName} {order.user.lastName}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-300 mt-0.5">
                {order.quantity > 1 && (
                  <span className="font-semibold">{order.quantity} × </span>
                )}
                {order.productName}
                {order.variantLabel && (
                  <span className="text-gray-400 dark:text-gray-500">
                    {" "}— {order.variantLabel}
                  </span>
                )}
              </div>
              {order.notes && (
                <div className="text-xs text-gray-400 dark:text-gray-500
                                italic mt-0.5 truncate max-w-xs">
                  "{order.notes}"
                </div>
              )}
              {lockBoxLabel && (
                <div className="mt-1 inline-flex items-center gap-1
                                text-xs font-semibold text-green-700
                                dark:text-green-400 bg-green-50
                                dark:bg-green-900/20 border
                                border-green-200 dark:border-green-700
                                px-2 py-0.5 rounded-full">
                  📦 {lockBoxLabel}
                  {order.lockBoxPin && (
                    <span className="ml-1 font-mono">
                      PIN: {order.lockBoxPin}
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className="flex-shrink-0 text-right">
              <div className={`text-sm font-semibold ${pickupUrgency}`}>
                📅 {pickupLabel}
              </div>
              <div className="text-xs text-gray-400 dark:text-gray-500">
                {pickupDate.toLocaleDateString("en-US", {
                  month: "short", day: "numeric", year: "numeric",
                })}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <span className={`inline-flex items-center gap-1 text-xs
                              font-bold px-2.5 py-1 rounded-full border
                              ${cfg.color}`}>
              {cfg.emoji} {cfg.label}
            </span>

            {cfg.next && !isFinished && (
              <button
                onClick={() => handleStatusChange(cfg.next)}
                disabled={updatingStatus}
                className="text-xs px-3 py-1 rounded-xl bg-farm-green
                           text-white font-semibold hover:bg-green-700
                           transition-colors disabled:opacity-50"
              >
                {updatingStatus ? "…" : cfg.nextLabel}
              </button>
            )}

            {!isFinished && (
              <button
                onClick={() => handleStatusChange("cancelled")}
                disabled={updatingStatus}
                className="text-xs px-3 py-1 rounded-xl border-2
                           border-red-300 dark:border-red-700 text-red-500
                           dark:text-red-400 hover:bg-red-50
                           dark:hover:bg-red-900/20 transition-colors
                           disabled:opacity-50"
              >
                Cancel
              </button>
            )}

            {order.status === "cancelled" && (
              <button
                onClick={() => handleStatusChange("ordered")}
                disabled={updatingStatus}
                className="text-xs px-3 py-1 rounded-xl border-2
                           border-gray-300 dark:border-gray-600
                           text-gray-500 dark:text-gray-400
                           hover:bg-gray-50 dark:hover:bg-gray-700
                           transition-colors disabled:opacity-50"
              >
                Reopen
              </button>
            )}

            <button
              onClick={onToggleExpand}
              className="ml-auto text-xs text-gray-400 dark:text-gray-500
                         hover:text-farm-green dark:hover:text-green-400
                         transition-colors"
            >
              {expanded ? "▲ Less" : "▼ Details"}
            </button>
          </div>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-gray-100 dark:border-gray-700
                        px-4 pb-4 pt-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <p className="text-xs font-semibold text-gray-500
                            dark:text-gray-400 uppercase tracking-wide">
                Order Details
              </p>
              <DetailRow label="Order #"
                         value={order.id.slice(0, 8).toUpperCase()} mono />
              <DetailRow
                label="Placed"
                value={new Date(order.createdAt).toLocaleDateString("en-US", {
                  month: "long", day: "numeric", year: "numeric",
                })}
              />
              <DetailRow
                label="Pickup"
                value={pickupDate.toLocaleDateString("en-US", {
                  weekday: "long", month: "long",
                  day: "numeric", year: "numeric",
                })}
              />
              <DetailRow label="Product"  value={order.productName} />
              <DetailRow label="Option"   value={order.variantLabel} />
              <DetailRow label="Quantity" value={`${order.quantity}`} />
              {order.notes && (
                <DetailRow label="Notes" value={order.notes} italic />
              )}
              {order.coupon?.code && (
                <DetailRow
                  label="Coupon"
                  value={`${order.coupon.code} (−${order.discount.toFixed(2)} cr)`}
                />
              )}
              <DetailRow
                label="Credits Charged"
                value={`${order.finalCost.toFixed(2)} credits`}
                highlight
              />
              {lockBoxLabel && (
                <DetailRow label="Delivery" value={lockBoxLabel} />
              )}
              {order.lockBoxPin && (
                <DetailRow label="Lock Box PIN"
                           value={order.lockBoxPin} mono />
              )}
            </div>

            <div className="space-y-2">
              <p className="text-xs font-semibold text-gray-500
                            dark:text-gray-400 uppercase tracking-wide">
                Customer
              </p>
              <DetailRow
                label="Name"
                value={`${order.user.firstName} ${order.user.lastName}`}
              />
              <DetailRow label="Email" value={order.user.email} />
              <DetailRow label="Account #"
                         value={order.user.accountNumber} mono />
              {order.fulfilledAt && (
                <DetailRow
                  label={
                    order.status === "cancelled"
                      ? "Cancelled at"
                      : "Completed at"
                  }
                  value={new Date(order.fulfilledAt).toLocaleString("en-US", {
                    month: "short", day: "numeric", year: "numeric",
                    hour: "numeric", minute: "2-digit", hour12: true,
                  })}
                />
              )}
            </div>
          </div>

          <div className="mt-4 pt-3 border-t border-gray-100
                          dark:border-gray-700">
            <p className="text-xs font-semibold text-gray-500
                          dark:text-gray-400 uppercase tracking-wide mb-2">
              Set Status Manually
            </p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                <button
                  key={key}
                  onClick={() => handleStatusChange(key)}
                  disabled={updatingStatus || order.status === key}
                  className={`text-xs px-3 py-1.5 rounded-xl border-2
                              font-semibold transition-all
                              ${order.status === key
                                ? `${config.color} cursor-default`
                                : "border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-farm-green dark:hover:border-green-500 bg-white dark:bg-gray-700"
                              } disabled:opacity-50`}
                >
                  {config.emoji} {config.label}
                  {order.status === key && " ✓"}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Detail row helper ──────────────────────────────────────────────────────
function DetailRow({ label, value, mono, italic, highlight }) {
  return (
    <div className="flex items-start justify-between gap-2 text-xs">
      <span className="text-gray-500 dark:text-gray-400 flex-shrink-0">
        {label}
      </span>
      <span className={`
        text-right break-all
        ${mono      ? "font-mono" : ""}
        ${italic    ? "italic"    : ""}
        ${highlight
          ? "font-bold text-farm-green dark:text-green-400"
          : "text-gray-700 dark:text-gray-300"
        }
      `}>
        {value}
      </span>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Analytics tab
// ═══════════════════════════════════════════════════════════════════════════
function AnalyticsTab() {
  const [subTab, setSubTab]                   = useState("overview");
  const [overview, setOverview]               = useState(null);
  const [customers, setCustomers]             = useState([]);
  const [recommendations, setRecommendations] = useState(null);
  const [loading, setLoading]                 = useState(true);
  const [coupons, setCoupons]                 = useState([]);
  const [sendModal, setSendModal]             = useState(null);
  const [message, setMessage]                 = useState({ text: "", type: "success" });

  const flash = (text, type = "success") => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: "", type: "success" }), 4000);
  };

  useEffect(() => {
    Promise.all([
      api.get("/analytics/overview"),
      api.get("/analytics/customers"),
      api.get("/analytics/recommendations"),
      api.get("/admin/coupons"),
    ])
      .then(([ov, cu, re, co]) => {
        setOverview(ov.data);
        setCustomers(cu.data);
        setRecommendations(re.data);
        setCoupons(co.data.filter((c) => c.isActive));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner />;

  return (
    <div className="space-y-5">
      {message.text && <FlashMessage message={message} />}

      <div className="flex gap-2">
        {[
          { id: "overview",        label: "Overview"        },
          { id: "customers",       label: "Customers"       },
          { id: "recommendations", label: "💡 Suggestions" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setSubTab(t.id)}
            className={`px-4 py-1.5 rounded-xl text-sm font-semibold
                        transition-colors
                        ${subTab === t.id
                          ? "bg-farm-green text-white dark:bg-green-700"
                          : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600"
                        }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {subTab === "overview"        && overview        && <OverviewPanel overview={overview} />}
      {subTab === "customers"       &&                    <CustomersPanel customers={customers} />}
      {subTab === "recommendations" && recommendations &&
        <RecommendationsPanel
          recommendations={recommendations}
          coupons={coupons}
          onSend={(coupon, customerIds) =>
            setSendModal({ coupon, customerIds })
          }
        />
      }

      {sendModal && (
        <QuickSendModal
          coupon={sendModal.coupon}
          customerIds={sendModal.customerIds}
          customers={customers}
          onClose={() => setSendModal(null)}
          onSent={(msg) => { setSendModal(null); flash(msg); }}
          onError={(msg) => flash(msg, "error")}
        />
      )}
    </div>
  );
}

function OverviewPanel({ overview }) {
  const { totals, last30, last7, productStats, revenueChart } = overview;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <KpiCard label="Total Orders"     value={totals.orders}
                 sub={`${last7.orders} this week`} />
        <KpiCard label="Orders (30 days)" value={last30.orders}
                 sub={`${last30.revenue.toFixed(1)} credits`} />
        <KpiCard label="Total Revenue"
                 value={`${totals.revenue.toFixed(0)} cr`}
                 sub={`${last30.revenue.toFixed(1)} last 30 days`} />
        <KpiCard label="Customers" value={totals.customers}
                 sub={`+${last30.newCustomers} last 30 days`} />
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-2xl border
                      border-farm-tan/30 dark:border-gray-700 p-6">
        <h3 className="font-bold text-farm-green dark:text-green-400 mb-4">
          Revenue — Last 30 Days (credits)
        </h3>
        <MiniBarChart data={revenueChart} />
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-2xl border
                      border-farm-tan/30 dark:border-gray-700 p-6">
        <h3 className="font-bold text-farm-green dark:text-green-400 mb-4">
          Product Performance
        </h3>
        {productStats.length === 0 ? (
          <p className="text-sm text-gray-400 dark:text-gray-500">
            No orders yet.
          </p>
        ) : (
          <div className="space-y-3">
            {productStats.map((p, i) => {
              const maxOrders = productStats[0].orderCount;
              const pct = maxOrders > 0
                ? Math.round((p.orderCount / maxOrders) * 100)
                : 0;
              return (
                <div key={p.name}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-medium text-gray-800
                                     dark:text-gray-100 truncate">
                      {i + 1}. {p.name}
                    </span>
                    <span className="text-gray-500 dark:text-gray-400
                                     ml-2 flex-shrink-0">
                      {p.orderCount} orders · {p.revenue.toFixed(1)} credits
                    </span>
                  </div>
                  <div className="h-2 bg-gray-100 dark:bg-gray-700
                                  rounded-full overflow-hidden">
                    <div
                      className="h-full bg-farm-green dark:bg-green-600
                                 rounded-full transition-all"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function MiniBarChart({ data }) {
  const max = Math.max(...data.map((d) => d.credits), 1);
  return (
    <div className="w-full">
      <div className="flex items-end gap-0.5 h-32">
        {data.map((d) => {
          const h = Math.max(4, Math.round((d.credits / max) * 100));
          return (
            <div key={d.date}
                 className="flex-1 flex flex-col items-center justify-end
                            group relative">
              <div
                className="w-full bg-farm-green dark:bg-green-600 rounded-t
                           opacity-80 group-hover:opacity-100 transition-all"
                style={{ height: `${h}%` }}
              />
              <div className="absolute bottom-full mb-1 hidden
                              group-hover:block z-10 bg-gray-900 text-white
                              text-xs rounded px-2 py-1 whitespace-nowrap
                              pointer-events-none">
                {d.date}: {d.credits} cr
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex mt-1">
        {data.map((d, i) => (
          <div key={d.date} className="flex-1 text-center">
            {i % 7 === 0 && (
              <span className="text-xs text-gray-400 dark:text-gray-500">
                {new Date(d.date).toLocaleDateString("en-US", {
                  month: "short", day: "numeric",
                })}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function CustomersPanel({ customers }) {
  const [search, setSearch]     = useState("");
  const [sortBy, setSortBy]     = useState("totalSpent");
  const [expanded, setExpanded] = useState(null);

  const sortOptions = [
    { id: "totalSpent",  label: "Total Spent"  },
    { id: "totalOrders", label: "Total Orders" },
    { id: "lastOrderAt", label: "Last Order"   },
    { id: "balance",     label: "Balance"      },
    { id: "firstName",   label: "Name"         },
  ];

  const filtered = customers
    .filter((c) => {
      const q = search.toLowerCase();
      return (
        c.firstName.toLowerCase().includes(q) ||
        c.lastName.toLowerCase().includes(q) ||
        c.email.toLowerCase().includes(q) ||
        c.accountNumber.toLowerCase().includes(q)
      );
    })
    .sort((a, b) => {
      if (sortBy === "firstName") return a.firstName.localeCompare(b.firstName);
      if (sortBy === "lastOrderAt") {
        return new Date(b.lastOrderAt ?? 0) - new Date(a.lastOrderAt ?? 0);
      }
      return (b[sortBy] ?? 0) - (a[sortBy] ?? 0);
    });

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="text" value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search customers…"
          className={inputCls + " flex-1"}
        />
        <select
          value={sortBy} onChange={(e) => setSortBy(e.target.value)}
          className={inputCls + " sm:w-44"}
        >
          {sortOptions.map((o) => (
            <option key={o.id} value={o.id}>{o.label}</option>
          ))}
        </select>
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        {filtered.length} customer{filtered.length !== 1 ? "s" : ""}
      </p>
      {filtered.map((c) => (
        <CustomerCard
          key={c.id}
          customer={c}
          expanded={expanded === c.id}
          onToggle={() => setExpanded(expanded === c.id ? null : c.id)}
        />
      ))}
    </div>
  );
}

function CustomerCard({ customer: c, expanded, onToggle }) {
  const activityColor =
    c.daysSinceLast === null
      ? "text-gray-400 dark:text-gray-500"
      : c.daysSinceLast <= 14
      ? "text-green-600 dark:text-green-400"
      : c.daysSinceLast <= 30
      ? "text-amber-600 dark:text-amber-400"
      : "text-red-500 dark:text-red-400";

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border
                    border-farm-tan/30 dark:border-gray-700 overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-5
                   hover:bg-gray-50 dark:hover:bg-gray-700/50
                   transition-colors text-left"
      >
        <div className="min-w-0">
          <div className="font-bold text-gray-800 dark:text-gray-100">
            {c.firstName} {c.lastName}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {c.email} · {c.accountNumber}
          </div>
        </div>
        <div className="flex items-center gap-4 ml-4 flex-shrink-0">
          <div className="text-right hidden sm:block">
            <div className="text-xs text-gray-500 dark:text-gray-400">Spent</div>
            <div className="font-bold text-farm-green dark:text-green-400">
              {c.totalSpent.toFixed(1)} cr
            </div>
          </div>
          <div className="text-right hidden sm:block">
            <div className="text-xs text-gray-500 dark:text-gray-400">Orders</div>
            <div className="font-bold text-gray-700 dark:text-gray-300">
              {c.totalOrders}
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-gray-500 dark:text-gray-400">Last order</div>
            <div className={`font-semibold text-sm ${activityColor}`}>
              {c.daysSinceLast === null
                ? "Never"
                : c.daysSinceLast === 0
                ? "Today"
                : `${c.daysSinceLast}d ago`}
            </div>
          </div>
          <svg
            className={`h-5 w-5 text-gray-400 dark:text-gray-500
                        transition-transform ${expanded ? "rotate-180" : ""}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round"
                  strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-gray-100 dark:border-gray-700
                        p-5 space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: "Balance",      value: `${c.balance.toFixed(2)} cr`      },
              { label: "Avg order",    value: `${c.avgOrderValue.toFixed(2)} cr` },
              { label: "Orders (30d)", value: c.ordersLast30                     },
              { label: "Favourite",    value: c.favouriteProduct ?? "—"          },
            ].map((s) => (
              <div key={s.label}
                   className="bg-gray-50 dark:bg-gray-700 rounded-xl p-3
                              border border-gray-100 dark:border-gray-600">
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {s.label}
                </div>
                <div className="font-bold text-sm text-gray-800
                                dark:text-gray-100 mt-0.5 truncate">
                  {s.value}
                </div>
              </div>
            ))}
          </div>

          {c.couponsUsed.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500
                            dark:text-gray-400 mb-1">
                Coupons used
              </p>
              <div className="flex flex-wrap gap-1">
                {c.couponsUsed.map((code) => (
                  <span key={code}
                        className="font-mono text-xs bg-green-50
                                   dark:bg-green-900/30 text-green-700
                                   dark:text-green-400 px-2 py-0.5
                                   rounded-full border border-green-200
                                   dark:border-green-700">
                    {code}
                  </span>
                ))}
              </div>
            </div>
          )}

          {c.recentOrders.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500
                            dark:text-gray-400 mb-2">
                Recent orders
              </p>
              <div className="space-y-1.5">
                {c.recentOrders.map((o) => (
                  <div key={o.id}
                       className="flex justify-between text-xs text-gray-600
                                  dark:text-gray-400 bg-gray-50
                                  dark:bg-gray-700/50 rounded-lg px-3 py-2">
                    <span>
                      {o.productName} — {o.variantLabel} × {o.quantity}
                    </span>
                    <span className="ml-2 flex-shrink-0">
                      {o.finalCost.toFixed(2)} cr ·{" "}
                      {new Date(o.createdAt).toLocaleDateString("en-US", {
                        month: "short", day: "numeric",
                      })}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function RecommendationsPanel({ recommendations, coupons, onSend }) {
  const [activeCoupon, setActiveCoupon] = useState(
    coupons.length > 0 ? coupons[0].id : ""
  );

  const sections = [
    {
      key:   "loyalHighFrequency",
      title: "🏆 Loyal High-Frequency Customers",
      desc:  "Ordered 3 or more times in the last 30 days — reward their loyalty.",
      color: "green",
    },
    {
      key:   "topSpenders",
      title: "💎 Top Spenders (VIP)",
      desc:  "Top 20% by total credits spent — make them feel valued.",
      color: "purple",
    },
    {
      key:   "newAndActive",
      title: "🌱 New & Already Active",
      desc:  "Joined in the last 14 days and already placed an order.",
      color: "blue",
    },
    {
      key:   "lapsed",
      title: "😴 Lapsed Customers",
      desc:  "Have not ordered in 31–90 days — a discount may re-engage them.",
      color: "amber",
    },
    {
      key:   "lowBalance",
      title: "💳 Low Balance",
      desc:  "Balance under 3 credits — a small discount lowers the barrier.",
      color: "orange",
    },
    {
      key:   "neverOrdered",
      title: "👋 Never Ordered",
      desc:  "Registered but have not placed a single order yet.",
      color: "gray",
    },
  ];

  const colorMap = {
    green:  "bg-green-50  dark:bg-green-900/20  border-green-200  dark:border-green-700  text-green-800  dark:text-green-300",
    purple: "bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-700 text-purple-800 dark:text-purple-300",
    blue:   "bg-blue-50   dark:bg-blue-900/20   border-blue-200   dark:border-blue-700   text-blue-800   dark:text-blue-300",
    amber:  "bg-amber-50  dark:bg-amber-900/20  border-amber-200  dark:border-amber-700  text-amber-800  dark:text-amber-300",
    orange: "bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-700 text-orange-800 dark:text-orange-300",
    gray:   "bg-gray-50   dark:bg-gray-700/30   border-gray-200   dark:border-gray-600   text-gray-700   dark:text-gray-300",
  };

  const selectedCoupon = coupons.find((c) => c.id === activeCoupon);

  return (
    <div className="space-y-5">
      <div className="bg-white dark:bg-gray-800 rounded-2xl border
                      border-farm-tan/30 dark:border-gray-700 p-5">
        <h3 className="font-bold text-farm-green dark:text-green-400 mb-3">
          Select Coupon to Send
        </h3>
        {coupons.length === 0 ? (
          <p className="text-sm text-amber-700 dark:text-amber-400">
            No active coupons. Create one in the Coupons tab first.
          </p>
        ) : (
          <div className="space-y-2">
            {coupons.map((c) => (
              <label
                key={c.id}
                className={`flex items-center gap-3 p-3 rounded-xl border-2
                            cursor-pointer transition-all
                            ${activeCoupon === c.id
                              ? "border-farm-green bg-green-50 dark:border-green-500 dark:bg-green-900/20"
                              : "border-gray-200 dark:border-gray-600 hover:border-farm-tan"
                            }`}
              >
                <input
                  type="radio" name="coupon" value={c.id}
                  checked={activeCoupon === c.id}
                  onChange={() => setActiveCoupon(c.id)}
                  className="accent-farm-green"
                />
                <span className="font-mono font-bold text-farm-green
                                 dark:text-green-400">
                  {c.code}
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {c.type === "fixed"
                    ? `${c.value} credits off`
                    : `${c.value}% off`}
                  {c.productName && (
                    <span className="ml-1 text-blue-600 dark:text-blue-400">
                      · only for {c.productName}
                    </span>
                  )}
                  {c.expiresAt
                    ? ` · expires ${new Date(c.expiresAt).toLocaleDateString()}`
                    : ""}
                </span>
              </label>
            ))}
          </div>
        )}
      </div>

      {sections.map((section) => {
        const list = recommendations[section.key] ?? [];
        if (list.length === 0) return null;
        return (
          <div key={section.key}
               className="bg-white dark:bg-gray-800 rounded-2xl border
                          border-farm-tan/30 dark:border-gray-700 p-5">
            <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
              <div>
                <h3 className="font-bold text-gray-800 dark:text-gray-100">
                  {section.title}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {section.desc}
                </p>
              </div>
              {selectedCoupon && (
                <button
                  onClick={() => onSend(selectedCoupon, list.map((c) => c.id))}
                  className="flex-shrink-0 bg-farm-green text-white px-4
                             py-1.5 rounded-xl text-sm font-semibold
                             hover:bg-green-700 transition-colors"
                >
                  📧 Send to All {list.length}
                </button>
              )}
            </div>
            <div className="space-y-2">
              {list.map((c) => (
                <div key={c.id}
                     className={`rounded-xl border p-3 ${colorMap[section.color]}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="font-semibold text-sm">
                        {c.firstName} {c.lastName}
                      </div>
                      <div className="text-xs opacity-80 mt-0.5 truncate">
                        {c.email}
                      </div>
                      <div className="text-xs opacity-70 mt-1 italic">
                        {c.reason}
                      </div>
                      <div className="text-xs opacity-60 mt-0.5">
                        💡 {c.suggestion}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                      {c.priority === "high" && (
                        <span className="text-xs font-bold bg-red-100
                                         dark:bg-red-900/40 text-red-600
                                         dark:text-red-400 px-2 py-0.5
                                         rounded-full">
                          High priority
                        </span>
                      )}
                      <div className="text-xs text-right opacity-80">
                        <div>
                          {c.totalOrders} order{c.totalOrders !== 1 ? "s" : ""}
                        </div>
                        <div>{c.totalSpent.toFixed(1)} cr spent</div>
                      </div>
                      {selectedCoupon && (
                        <button
                          onClick={() => onSend(selectedCoupon, [c.id])}
                          className="text-xs px-3 py-1.5 rounded-lg border-2
                                     border-current font-semibold
                                     hover:opacity-70 transition-opacity"
                        >
                          📧 Send
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {sections.every((s) => (recommendations[s.key] ?? []).length === 0) && (
        <p className="text-center text-gray-400 dark:text-gray-500 py-12">
          Not enough order data yet to generate recommendations.
        </p>
      )}
    </div>
  );
}

function QuickSendModal({
  coupon, customerIds, customers, onClose, onSent, onError,
}) {
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const targets = customers.filter((c) => customerIds.includes(c.id));

  const handleSend = async () => {
    setSending(true);
    try {
      const { data } = await api.post(
        `/admin/coupons/${coupon.id}/send`,
        { customerIds, sendToAll: false, message }
      );
      onSent(data.message);
    } catch (err) {
      onError(err.response?.data?.error || "Send failed.");
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center
                 bg-black/50 px-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl
                      w-full max-w-md border border-farm-tan/30
                      dark:border-gray-700">
        <div className="flex items-center justify-between p-6 border-b
                        border-gray-100 dark:border-gray-700">
          <h3 className="font-bold text-farm-green dark:text-green-400 text-lg">
            Confirm Send
          </h3>
          <button onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
            ×
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="bg-green-50 dark:bg-green-900/20 border
                          border-green-200 dark:border-green-700 rounded-xl
                          p-4 text-sm text-green-800 dark:text-green-300">
            Sending{" "}
            <span className="font-mono font-bold">{coupon.code}</span> (
            {coupon.type === "fixed"
              ? `${coupon.value} credits off`
              : `${coupon.value}% off`}
            ) to{" "}
            <strong>
              {customerIds.length} customer{customerIds.length !== 1 ? "s" : ""}
            </strong>.
          </div>
          {targets.length > 0 && (
            <div className="max-h-32 overflow-y-auto space-y-1">
              {targets.map((c) => (
                <div key={c.id}
                     className="text-xs text-gray-600 dark:text-gray-400
                                bg-gray-50 dark:bg-gray-700/50 rounded-lg
                                px-3 py-1.5">
                  {c.firstName} {c.lastName} — {c.email}
                </div>
              ))}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700
                              dark:text-gray-300 mb-1">
              Personal message{" "}
              <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea rows={2} value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="e.g. Thank you for being a valued customer!"
                      className={inputCls + " resize-none"} />
          </div>
        </div>
        <div className="p-6 border-t border-gray-100 dark:border-gray-700 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 border-2 border-gray-300 dark:border-gray-600
                       text-gray-600 dark:text-gray-400 py-2.5 rounded-xl
                       font-semibold hover:bg-gray-50 dark:hover:bg-gray-700
                       transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSend} disabled={sending}
            className="flex-1 bg-farm-green text-white py-2.5 rounded-xl
                       font-semibold hover:bg-green-700 transition-colors
                       disabled:opacity-60"
          >
            {sending
              ? "Sending…"
              : `Send to ${customerIds.length} Customer${customerIds.length !== 1 ? "s" : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Credits tab
// ═══════════════════════════════════════════════════════════════════════════
function CreditsTab() {
  const [customers, setCustomers]             = useState([]);
  const [history, setHistory]                 = useState([]);
  const [loadingHistory, setLoadingHistory]   = useState(true);
  const [message, setMessage]                 = useState({ text: "", type: "success" });
  const [search, setSearch]                   = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [amount, setAmount]                   = useState("");
  const [note, setNote]                       = useState("");
  const [saving, setSaving]                   = useState(false);
  const [showDropdown, setShowDropdown]       = useState(false);

  const flash = (text, type = "success") => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: "", type: "success" }), 4000);
  };

  const loadHistory = useCallback(() => {
    setLoadingHistory(true);
    api.get("/admin/credits/history")
      .then(({ data }) => setHistory(data))
      .catch(console.error)
      .finally(() => setLoadingHistory(false));
  }, []);

  useEffect(() => {
    api.get("/admin/customers")
      .then(({ data }) => setCustomers(data))
      .catch(console.error);
    loadHistory();
  }, [loadHistory]);

  const filtered = search.trim().length > 0
    ? customers.filter((c) => {
        const q = search.toLowerCase();
        return (
          c.firstName.toLowerCase().includes(q) ||
          c.lastName.toLowerCase().includes(q) ||
          c.email.toLowerCase().includes(q) ||
          c.accountNumber.toLowerCase().includes(q)
        );
      }).slice(0, 8)
    : [];

  const handleSelectCustomer = (c) => {
    setSelectedCustomer(c);
    setSearch(`${c.firstName} ${c.lastName}`);
    setShowDropdown(false);
  };

  const handleClearCustomer = () => {
    setSelectedCustomer(null);
    setSearch("");
    setAmount("");
    setNote("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCustomer) return flash("Please select a customer.", "error");
    const credits = Number(amount);
    if (!credits || credits <= 0) {
      return flash("Amount must be a positive number.", "error");
    }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/credits/add", {
        userId: selectedCustomer.id,
        amount: credits,
        note:   note.trim() || null,
      });
      flash(data.message);
      setSelectedCustomer((prev) =>
        prev ? { ...prev, balance: data.newBalance } : null
      );
      setCustomers((prev) =>
        prev.map((c) =>
          c.id === data.user.id ? { ...c, balance: data.newBalance } : c
        )
      );
      setAmount("");
      setNote("");
      loadHistory();
    } catch (err) {
      flash(err.response?.data?.error || "Failed to add credits.", "error");
    } finally {
      setSaving(false);
    }
  };

  const QUICK_AMOUNTS = [5, 10, 15, 20, 25, 50];

  return (
    <div className="space-y-6">
      {message.text && <FlashMessage message={message} />}

      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6
                      border border-farm-tan/30 dark:border-gray-700">
        <h3 className="text-lg font-bold text-farm-green dark:text-green-400 mb-5">
          Add Credits to Account
        </h3>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Customer search */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700
                              dark:text-gray-300 mb-1">
              Customer <span className="text-red-500">*</span>
            </label>

            {selectedCustomer ? (
              <div className="flex items-center justify-between bg-green-50
                              dark:bg-green-900/20 border-2 border-farm-green
                              dark:border-green-500 rounded-xl px-4 py-3">
                <div>
                  <div className="font-bold text-gray-800 dark:text-gray-100">
                    {selectedCustomer.firstName} {selectedCustomer.lastName}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {selectedCustomer.email} · {selectedCustomer.accountNumber}
                  </div>
                  <div className="text-sm font-semibold text-farm-green
                                  dark:text-green-400 mt-0.5">
                    Current balance:{" "}
                    {selectedCustomer.balance?.toFixed(2)} credits
                  </div>
                </div>
                <button
                  type="button" onClick={handleClearCustomer}
                  className="text-gray-400 hover:text-red-500
                             dark:hover:text-red-400 transition-colors
                             text-2xl leading-none ml-3"
                >
                  ×
                </button>
              </div>
            ) : (
              <div>
                <input
                  type="text" value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setShowDropdown(true);
                  }}
                  onFocus={() => setShowDropdown(true)}
                  onBlur={() =>
                    setTimeout(() => setShowDropdown(false), 150)
                  }
                  placeholder="Search by name, email, or account #…"
                  autoComplete="off"
                  className={inputCls}
                />

                {showDropdown && filtered.length > 0 && (
                  <div className="absolute z-20 w-full mt-1 bg-white
                                  dark:bg-gray-800 border border-gray-200
                                  dark:border-gray-600 rounded-xl shadow-xl
                                  overflow-hidden">
                    {filtered.map((c) => (
                      <button
                        key={c.id} type="button"
                        onMouseDown={() => handleSelectCustomer(c)}
                        className="w-full flex items-center justify-between
                                   px-4 py-3 text-left hover:bg-green-50
                                   dark:hover:bg-green-900/20 border-b
                                   last:border-b-0 border-gray-100
                                   dark:border-gray-700 transition-colors"
                      >
                        <div className="min-w-0">
                          <div className="font-semibold text-sm text-gray-800
                                          dark:text-gray-100 truncate">
                            {c.firstName} {c.lastName}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {c.email}
                          </div>
                        </div>
                        <div className="ml-3 flex-shrink-0 text-right">
                          <div className="font-mono text-xs text-gray-400 dark:text-gray-500">
                            {c.accountNumber}
                          </div>
                          <div className="text-xs font-semibold text-farm-green dark:text-green-400">
                            {c.balance?.toFixed(2)} cr
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {showDropdown && search.trim().length > 0 &&
                  filtered.length === 0 && (
                    <div className="absolute z-20 w-full mt-1 bg-white
                                    dark:bg-gray-800 border border-gray-200
                                    dark:border-gray-600 rounded-xl shadow-xl
                                    px-4 py-3 text-sm text-gray-400
                                    dark:text-gray-500">
                      No customers found.
                    </div>
                  )}
              </div>
            )}
          </div>

          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700
                              dark:text-gray-300 mb-2">
              Credits to Add <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-6 gap-2 mb-3">
              {QUICK_AMOUNTS.map((a) => (
                <button
                  key={a} type="button"
                  onClick={() => setAmount(String(a))}
                  className={`py-2 rounded-xl border-2 text-sm font-bold
                              transition-all
                              ${Number(amount) === a
                                ? "border-farm-green bg-green-50 text-farm-green dark:border-green-500 dark:bg-green-900/30 dark:text-green-400"
                                : "border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-farm-tan bg-white dark:bg-gray-700"
                              }`}
                >
                  {a}
                </button>
              ))}
            </div>
            <div className="relative">
              <input
                type="number" min={0.5} step={0.5} value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Or enter a custom amount"
                className={inputCls + " pr-16"}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2
                               text-xs text-gray-400 dark:text-gray-500
                               pointer-events-none">
                credits
              </span>
            </div>
            {selectedCustomer && Number(amount) > 0 && (
              <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                New balance after adding:{" "}
                <span className="font-bold text-farm-green dark:text-green-400">
                  {(
                    (selectedCustomer.balance ?? 0) + Number(amount)
                  ).toFixed(2)}{" "}
                  credits
                </span>
              </div>
            )}
          </div>

          {/* Note */}
          <div>
            <label className="block text-sm font-medium text-gray-700
                              dark:text-gray-300 mb-1">
              Note / Reason{" "}
              <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              type="text" value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Cash payment received at pickup"
              className={inputCls}
            />
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
              Saved in the credit log for your records.
            </p>
          </div>

          <button
            type="submit"
            disabled={saving || !selectedCustomer || !Number(amount)}
            className="w-full bg-farm-green text-white py-3 rounded-xl
                       font-semibold text-lg hover:bg-green-700
                       transition-colors disabled:opacity-60"
          >
            {saving
              ? "Adding Credits…"
              : `Add ${Number(amount) > 0 ? Number(amount) : ""} Credits`}
          </button>
        </form>
      </div>

      {/* History */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6
                      border border-farm-tan/30 dark:border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-farm-green dark:text-green-400">
            Recent Manual Credit Additions
          </h3>
          <button onClick={loadHistory}
                  className="text-xs text-farm-green dark:text-green-400 hover:underline">
            Refresh
          </button>
        </div>

        {loadingHistory ? (
          <Spinner />
        ) : history.length === 0 ? (
          <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-6">
            No manual credit additions yet.
          </p>
        ) : (
          <div className="space-y-2">
            {history.map((h) => (
              <div
                key={h.id}
                className="flex items-center justify-between bg-gray-50
                           dark:bg-gray-700/50 rounded-xl px-4 py-3 border
                           border-gray-100 dark:border-gray-600"
              >
                <div className="min-w-0">
                  <div className="font-semibold text-sm text-gray-800
                                  dark:text-gray-100">
                    {h.user.firstName} {h.user.lastName}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {h.user.email} · {h.user.accountNumber}
                  </div>
                  {h.txHash && (
                    <div className="text-xs text-gray-400 dark:text-gray-500
                                    italic mt-0.5 truncate">
                      "{h.txHash}"
                    </div>
                  )}
                </div>
                <div className="ml-4 flex-shrink-0 text-right">
                  <div className="font-bold text-farm-green dark:text-green-400">
                    +{h.amount.toFixed(2)} cr
                  </div>
                  <div className="text-xs text-gray-400 dark:text-gray-500">
                    {new Date(h.createdAt).toLocaleDateString("en-US", {
                      month: "short", day: "numeric", year: "numeric",
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Email tab
// ═══════════════════════════════════════════════════════════════════════════
function EmailTab() {
  const [customers, setCustomers]     = useState([]);
  const [mode, setMode]               = useState("select");
  const [search, setSearch]           = useState("");
  const [selected, setSelected]       = useState([]);
  const [subject, setSubject]         = useState("");
  const [htmlBody, setHtmlBody]       = useState("");
  const [sending, setSending]         = useState(false);
  const [previewing, setPreviewing]   = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [message, setMessage]         = useState({ text: "", type: "success" });

  const flash = (text, type = "success") => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: "", type: "success" }), 5000);
  };

  useEffect(() => {
    api.get("/admin/customers")
      .then(({ data }) => setCustomers(data))
      .catch(console.error);
  }, []);

  const filtered = customers.filter((c) => {
    const q = search.toLowerCase();
    return (
      c.firstName.toLowerCase().includes(q) ||
      c.lastName.toLowerCase().includes(q) ||
      c.email.toLowerCase().includes(q) ||
      c.accountNumber.toLowerCase().includes(q)
    );
  });

  const toggleCustomer = (id) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const recipientCount =
    mode === "all" ? customers.length : selected.length;

  const handlePreview = async () => {
    if (!subject.trim() || !htmlBody.trim()) {
      flash("Add a subject and message body before previewing.", "error");
      return;
    }
    setPreviewing(true);
    try {
      const { data } = await api.post("/admin/email/preview", {
        subject,
        htmlBody,
        firstName:
          mode === "select" && selected.length > 0
            ? customers.find((c) => c.id === selected[0])?.firstName ?? "Jane"
            : customers[0]?.firstName ?? "Jane",
      });
      setPreviewHtml(data.previewHtml);
      setShowPreview(true);
    } catch (err) {
      flash(err.response?.data?.error || "Preview failed.", "error");
    } finally {
      setPreviewing(false);
    }
  };

  const handleSend = async () => {
    if (!subject.trim())  return flash("Subject is required.", "error");
    if (!htmlBody.trim()) return flash("Message body is required.", "error");
    if (mode === "select" && selected.length === 0) {
      return flash("Select at least one customer.", "error");
    }
    setSending(true);
    try {
      const { data } = await api.post("/admin/email/send", {
        subject,
        htmlBody,
        customerIds: mode === "select" ? selected : [],
        sendToAll:   mode === "all",
      });
      flash(data.message);
      setSubject("");
      setHtmlBody("");
      setSelected([]);
      setMode("select");
    } catch (err) {
      flash(err.response?.data?.error || "Send failed.", "error");
    } finally {
      setSending(false);
    }
  };

  const SNIPPETS = [
    {
      label: "Heading",
      html:  '<h2 style="color:#4a7c59;margin-top:0;">Your heading here</h2>',
    },
    {
      label: "Paragraph",
      html:  '<p style="color:#555;font-size:14px;line-height:1.6;">Your paragraph text here.</p>',
    },
    {
      label: "Button",
      html:  `<div style="text-align:center;margin:24px 0;">
  <a href="https://app.cedarforkfarm.com"
     style="background:#4a7c59;color:#ffffff;padding:12px 28px;
            border-radius:8px;text-decoration:none;font-weight:bold;
            font-size:15px;display:inline-block;">
    Shop Now →
  </a>
</div>`,
    },
    {
      label: "Divider",
      html:  '<hr style="border:none;border-top:1px solid #eee;margin:20px 0;"/>',
    },
    {
      label: "Info box",
      html:  `<div style="background:#f0faf4;border-left:4px solid #4a7c59;
          padding:14px 16px;border-radius:4px;margin:16px 0;">
  <p style="margin:0;color:#2e7d32;font-size:14px;">
    📢 Your announcement text here.
  </p>
</div>`,
    },
    {
      label: "Warning box",
      html:  `<div style="background:#fff8e1;border:1px solid #ffe082;
          border-radius:8px;padding:14px 16px;margin:16px 0;">
  <p style="margin:0;font-size:13px;color:#795548;">
    ⚠️ Your warning message here.
  </p>
</div>`,
    },
  ];

  const insertSnippet = (html) => {
    setHtmlBody((prev) => (prev ? prev + "\n\n" + html : html));
  };

  return (
    <div className="space-y-5">
      {message.text && <FlashMessage message={message} />}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Left — compose */}
        <div className="space-y-5">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg
                          p-6 border border-farm-tan/30 dark:border-gray-700">
            <h3 className="text-lg font-bold text-farm-green dark:text-green-400 mb-4">
              ✉️ Compose Email
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700
                                  dark:text-gray-300 mb-1">
                  Subject <span className="text-red-500">*</span>
                </label>
                <input
                  type="text" value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="e.g. New products now available!"
                  className={inputCls}
                />
              </div>

              <div>
                <p className="text-xs font-medium text-gray-500
                              dark:text-gray-400 mb-2">
                  Insert snippet:
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {SNIPPETS.map((s) => (
                    <button
                      key={s.label} type="button"
                      onClick={() => insertSnippet(s.html)}
                      className="text-xs px-2.5 py-1 rounded-lg border
                                 border-gray-200 dark:border-gray-600
                                 text-gray-600 dark:text-gray-400
                                 hover:border-farm-green
                                 dark:hover:border-green-500
                                 hover:text-farm-green
                                 dark:hover:text-green-400
                                 bg-white dark:bg-gray-700 transition-colors"
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700
                                  dark:text-gray-300 mb-1">
                  Message Body (HTML) <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={htmlBody}
                  onChange={(e) => setHtmlBody(e.target.value)}
                  rows={16}
                  placeholder={`<h2 style="color:#4a7c59;margin-top:0;">Hello from Cedar Fork Farm!</h2>\n<p style="color:#555;font-size:14px;line-height:1.6;">Your message goes here...</p>`}
                  spellCheck={false}
                  className={inputCls + " font-mono text-xs resize-y leading-relaxed"}
                />
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  Standard HTML and inline styles are supported. The email
                  will be wrapped in the Cedar Fork Farm template
                  automatically.
                </p>
              </div>

              <button
                type="button" onClick={handlePreview}
                disabled={previewing || !subject.trim() || !htmlBody.trim()}
                className="w-full border-2 border-farm-green text-farm-green
                           dark:border-green-400 dark:text-green-400
                           py-2.5 rounded-xl font-semibold hover:bg-farm-green
                           hover:text-white dark:hover:bg-green-700
                           dark:hover:text-white transition-colors
                           disabled:opacity-50"
              >
                {previewing ? "Generating preview…" : "👁️ Preview Email"}
              </button>
            </div>
          </div>
        </div>

        {/* Right — recipients */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6
                        border border-farm-tan/30 dark:border-gray-700">
          <h3 className="text-lg font-bold text-farm-green dark:text-green-400 mb-4">
            Recipients
          </h3>

          <div className="grid grid-cols-2 gap-3 mb-4">
            {[
              { id: "select", label: "🎯 Specific customers" },
              { id: "all",    label: "📣 All customers"      },
            ].map((m) => (
              <button
                key={m.id} type="button" onClick={() => setMode(m.id)}
                className={`p-3 rounded-xl border-2 text-sm font-medium
                            transition-all
                            ${mode === m.id
                              ? "border-farm-green bg-green-50 text-farm-green dark:border-green-500 dark:bg-green-900/30 dark:text-green-400"
                              : "border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-farm-tan"
                            }`}
              >
                {m.label}
              </button>
            ))}
          </div>

          {mode === "all" && (
            <div className="bg-amber-50 dark:bg-amber-900/30 border
                            border-amber-300 dark:border-amber-600 rounded-xl
                            p-4 mb-4 text-sm text-amber-800 dark:text-amber-300">
              📣 This will send to all{" "}
              <strong>{customers.length}</strong> customers. Each customer
              receives their own individual email.
            </div>
          )}

          {mode === "select" && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700
                                  dark:text-gray-300">
                  Select customers{" "}
                  {selected.length > 0 && (
                    <span className="text-farm-green dark:text-green-400 ml-1">
                      ({selected.length} selected)
                    </span>
                  )}
                </label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setSelected(filtered.map((c) => c.id))}
                    className="text-xs text-farm-green dark:text-green-400 hover:underline"
                  >
                    Select all
                  </button>
                  {selected.length > 0 && (
                    <button type="button" onClick={() => setSelected([])}
                            className="text-xs text-gray-400 hover:underline">
                      Clear
                    </button>
                  )}
                </div>
              </div>

              <input
                type="text" value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, email, or account #…"
                className={inputCls}
              />

              <div className="border border-gray-200 dark:border-gray-600
                              rounded-xl overflow-hidden max-h-80 overflow-y-auto">
                {filtered.length === 0 ? (
                  <p className="text-sm text-gray-400 dark:text-gray-500
                                text-center py-6">
                    No customers found.
                  </p>
                ) : (
                  filtered.map((c) => {
                    const isSel = selected.includes(c.id);
                    return (
                      <button
                        key={c.id} type="button"
                        onClick={() => toggleCustomer(c.id)}
                        className={`w-full flex items-center gap-3 px-4 py-3
                                    text-left border-b last:border-b-0
                                    border-gray-100 dark:border-gray-700
                                    transition-colors
                                    ${isSel
                                      ? "bg-green-50 dark:bg-green-900/20"
                                      : "hover:bg-gray-50 dark:hover:bg-gray-700/50"
                                    }`}
                      >
                        <div className={`h-5 w-5 rounded border-2 flex-shrink-0
                                         flex items-center justify-center
                                         ${isSel
                                           ? "bg-farm-green border-farm-green dark:bg-green-600 dark:border-green-600"
                                           : "border-gray-300 dark:border-gray-600"
                                         }`}>
                          {isSel && (
                            <svg className="h-3 w-3 text-white"
                                 viewBox="0 0 12 12" fill="currentColor">
                              <path d="M10 3L5 8.5 2 5.5" stroke="white"
                                    strokeWidth="2" fill="none"
                                    strokeLinecap="round"
                                    strokeLinejoin="round" />
                            </svg>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-sm text-gray-800
                                          dark:text-gray-100 truncate">
                            {c.firstName} {c.lastName}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {c.email}
                          </div>
                        </div>
                        <div className="font-mono text-xs text-gray-400
                                        dark:text-gray-500 flex-shrink-0">
                          {c.accountNumber}
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          )}

          <div className="mt-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3
                          border border-gray-200 dark:border-gray-600
                          text-xs text-gray-500 dark:text-gray-400">
            🔒 Each customer receives their own individual email. No
            recipient can see other recipients' addresses.
          </div>

          <div className="mt-4 space-y-3">
            {recipientCount > 0 && (
              <div className="text-sm text-center text-gray-600 dark:text-gray-400">
                Ready to send to{" "}
                <strong className="text-farm-green dark:text-green-400">
                  {recipientCount} customer{recipientCount !== 1 ? "s" : ""}
                </strong>
              </div>
            )}
            <button
              type="button" onClick={handleSend}
              disabled={
                sending ||
                !subject.trim() ||
                !htmlBody.trim() ||
                recipientCount === 0
              }
              className="w-full bg-farm-green text-white py-3 rounded-xl
                         font-semibold text-lg hover:bg-green-700
                         transition-colors disabled:opacity-60"
            >
              {sending
                ? "Sending…"
                : `Send Email to ${
                    recipientCount > 0 ? recipientCount : ""
                  } Customer${recipientCount !== 1 ? "s" : ""}`}
            </button>
          </div>
        </div>
      </div>

      {showPreview && (
        <EmailPreviewModal
          subject={subject}
          previewHtml={previewHtml}
          recipientCount={recipientCount}
          sending={sending}
          onClose={() => setShowPreview(false)}
          onSend={() => { setShowPreview(false); handleSend(); }}
        />
      )}
    </div>
  );
}

// ── Email preview modal ────────────────────────────────────────────────────
function EmailPreviewModal({
  subject, previewHtml, recipientCount, sending, onClose, onSend,
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center
                 bg-black/60 px-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl
                      w-full max-w-2xl max-h-[90vh] flex flex-col
                      border border-farm-tan/30 dark:border-gray-700">
        <div className="flex items-center justify-between p-5 border-b
                        border-gray-100 dark:border-gray-700 flex-shrink-0">
          <div className="min-w-0">
            <h3 className="font-bold text-farm-green dark:text-green-400 text-lg">
              Email Preview
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
              Subject: <span className="font-semibold">{subject}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300
                       text-2xl leading-none ml-4 flex-shrink-0"
          >
            ×
          </button>
        </div>

        <PreviewFrame html={previewHtml} />

        <div className="p-5 border-t border-gray-100 dark:border-gray-700
                        flex gap-3 flex-shrink-0">
          <button
            onClick={onClose}
            className="flex-1 border-2 border-gray-300 dark:border-gray-600
                       text-gray-600 dark:text-gray-400 py-2.5 rounded-xl
                       font-semibold hover:bg-gray-50 dark:hover:bg-gray-700
                       transition-colors"
          >
            ← Edit
          </button>
          <button
            onClick={onSend}
            disabled={sending || recipientCount === 0}
            className="flex-1 bg-farm-green text-white py-2.5 rounded-xl
                       font-semibold hover:bg-green-700 transition-colors
                       disabled:opacity-60"
          >
            {sending
              ? "Sending…"
              : `Send to ${recipientCount} Customer${recipientCount !== 1 ? "s" : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Preview frame ──────────────────────────────────────────────────────────
function PreviewFrame({ html }) {
  const [view, setView] = useState("desktop");
  const iframeRef       = React.useRef(null);

  React.useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) return;
    doc.open();
    doc.write(html);
    doc.close();
  }, [html]);

  return (
    <div className="flex-1 overflow-hidden flex flex-col">
      <div className="flex items-center gap-2 px-5 py-3 border-b
                      border-gray-100 dark:border-gray-700 flex-shrink-0">
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 mr-1">
          Preview as:
        </span>
        {[
          { id: "desktop", label: "🖥️ Desktop" },
          { id: "mobile",  label: "📱 Mobile"  },
        ].map((v) => (
          <button
            key={v.id} onClick={() => setView(v.id)}
            className={`text-xs px-3 py-1 rounded-lg font-semibold
                        transition-colors
                        ${view === v.id
                          ? "bg-farm-green text-white dark:bg-green-700"
                          : "bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200"
                        }`}
          >
            {v.label}
          </button>
        ))}
        <span className="ml-auto text-xs text-gray-400 dark:text-gray-500">
          Pixel-accurate preview.
        </span>
      </div>

      <div className="flex-1 overflow-auto bg-gray-100 dark:bg-gray-900
                      flex items-start justify-center p-4">
        <div
          className="transition-all duration-300 bg-white shadow-xl
                     rounded-lg overflow-hidden"
          style={{
            width:    view === "mobile" ? "390px" : "100%",
            maxWidth: view === "mobile" ? "390px" : "700px",
          }}
        >
          <iframe
            ref={iframeRef}
            title="Email preview"
            className="w-full border-0"
            style={{ minHeight: "500px", height: "600px" }}
            sandbox="allow-same-origin"
          />
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Shared sub-components
// ═══════════════════════════════════════════════════════════════════════════
function VariantRow({ variant, onToggle, onPriceSave }) {
  const [editing, setEditing] = useState(false);
  const [price, setPrice]     = useState(variant.price);
  const [saving, setSaving]   = useState(false);

  const handleSave = async () => {
    setSaving(true);
    const ok = await onPriceSave(price);
    setSaving(false);
    if (ok) setEditing(false);
  };

  return (
    <div className="flex items-center justify-between bg-gray-50
                    dark:bg-gray-700/50 rounded-xl px-4 py-2 border
                    border-gray-100 dark:border-gray-600">
      <span className={`text-sm font-medium ${
        variant.isVisible
          ? "text-gray-800 dark:text-gray-100"
          : "text-gray-400 dark:text-gray-500 line-through"
      }`}>
        {variant.label}
      </span>
      <div className="flex items-center gap-3 flex-shrink-0 ml-3">
        {editing ? (
          <div className="flex items-center gap-1">
            <input
              type="number" min={0.5} step={0.5} value={price}
              onChange={(e) => setPrice(e.target.value)}
              className="w-20 border border-gray-300 dark:border-gray-600
                         rounded-lg px-2 py-1 text-sm bg-white
                         dark:bg-gray-700 text-gray-800 dark:text-gray-100
                         focus:outline-none focus:ring-2 focus:ring-farm-green"
            />
            <button onClick={handleSave} disabled={saving}
                    className="text-xs text-farm-green dark:text-green-400
                               font-semibold hover:underline disabled:opacity-50">
              {saving ? "…" : "Save"}
            </button>
            <button
              onClick={() => { setPrice(variant.price); setEditing(false); }}
              className="text-xs text-gray-400 dark:text-gray-500 hover:underline"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button onClick={() => setEditing(true)} title="Click to edit price"
                  className="text-sm font-bold text-farm-green dark:text-green-400
                             hover:underline">
            {variant.price} credits ✎
          </button>
        )}
        <button
          onClick={onToggle}
          className={`text-xs px-2 py-1 rounded-lg font-semibold
                      transition-colors
                      ${variant.isVisible
                        ? "bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400"
                        : "bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-400"
                      }`}
        >
          {variant.isVisible ? "Shown" : "Hidden"}
        </button>
      </div>
    </div>
  );
}

function AddVariantForm({ productId, onAdd }) {
  const [open, setOpen]     = useState(false);
  const [label, setLabel]   = useState("");
  const [price, setPrice]   = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!label.trim()) return setError("Label is required.");
    if (!price || Number(price) <= 0) return setError("Price must be positive.");
    setSaving(true);
    const ok = await onAdd(productId, label.trim(), Number(price));
    setSaving(false);
    if (ok) { setLabel(""); setPrice(""); setOpen(false); }
  };

  if (!open) {
    return (
      <button
        type="button" onClick={() => setOpen(true)}
        className="w-full mt-1 py-2 rounded-xl border-2 border-dashed
                   border-gray-300 dark:border-gray-600 text-sm
                   text-gray-400 dark:text-gray-500
                   hover:border-farm-green dark:hover:border-green-500
                   hover:text-farm-green dark:hover:text-green-400
                   transition-colors"
      >
        + Add option / variant
      </button>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="mt-1 bg-blue-50 dark:bg-blue-900/20 border border-blue-200
                 dark:border-blue-700 rounded-xl p-4 space-y-3"
    >
      <p className="text-sm font-semibold text-blue-800 dark:text-blue-300">
        New Option
      </p>
      {error && (
        <p className="text-xs text-red-500 dark:text-red-400">{error}</p>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600
                            dark:text-gray-400 mb-1">Label</label>
          <input type="text" required value={label}
                 onChange={(e) => setLabel(e.target.value)}
                 placeholder="e.g. 12 count" className={inputCls} />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600
                            dark:text-gray-400 mb-1">Price (credits)</label>
          <input type="number" required min={0.5} step={0.5} value={price}
                 onChange={(e) => setPrice(e.target.value)}
                 placeholder="e.g. 5" className={inputCls} />
        </div>
      </div>
      <div className="flex gap-2">
        <button type="submit" disabled={saving}
                className="flex-1 bg-farm-green text-white py-2 rounded-xl
                           text-sm font-semibold hover:bg-green-700
                           transition-colors disabled:opacity-60">
          {saving ? "Adding…" : "Add Option"}
        </button>
        <button type="button"
                onClick={() => { setOpen(false); setLabel(""); setPrice(""); setError(""); }}
                className="flex-1 border-2 border-gray-300 dark:border-gray-600
                           text-gray-600 dark:text-gray-400 py-2 rounded-xl
                           text-sm hover:bg-gray-50 dark:hover:bg-gray-700
                           transition-colors">
          Cancel
        </button>
      </div>
    </form>
  );
}

function KpiCard({ label, value, sub }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border
                    border-farm-tan/30 dark:border-gray-700 p-4">
      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
        {label}
      </div>
      <div className="text-2xl font-bold text-farm-green dark:text-green-400">
        {value}
      </div>
      {sub && (
        <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
          {sub}
        </div>
      )}
    </div>
  );
}

function FlashMessage({ message }) {
  return (
    <div className={`rounded-lg p-3 text-sm border ${
      message.type === "error"
        ? "bg-red-50 dark:bg-red-900/30 border-red-300 dark:border-red-600 text-red-700 dark:text-red-300"
        : "bg-green-50 dark:bg-green-900/30 border-green-300 dark:border-green-600 text-green-700 dark:text-green-300"
    }`}>
      {message.text}
    </div>
  );
}

function Spinner() {
  return (
    <div className="flex justify-center py-12">
      <div className="animate-spin h-10 w-10 rounded-full border-4
                      border-farm-green border-t-transparent" />
    </div>
  );
}

const inputCls =
  "w-full border border-gray-300 dark:border-gray-600 rounded-lg " +
  "px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 " +
  "dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 " +
  "focus:outline-none focus:ring-2 focus:ring-farm-green";
